const restrictedProductMessage = "Sorry, you can only purchase a maximum of three units of this item.";

const getDiscountCodeSettings = () => {
  const promoCodeSettingsEl = document.querySelector("#promo-code-settings");

  return promoCodeSettingsEl
    ? JSON.parse(promoCodeSettingsEl?.innerHTML || "null")
    : {};
};

/**
 * @type {Object.<string, string>}
 */
const DKCurrencyLocaleMap = {
  USD: "en-US", // United States Dollar
  GBP: "en-GB", // British Pound
  EUR: "de-DE", // Euro
  CAD: "en-CA", // Canadian Dollar
  AUD: "en-AU", // Australian Dollar
  NZD: "en-NZ", // New Zealand Dollar
  INR: "en-IN", // Indian Rupee
  ZAR: "en-ZA", // South African Rand
  JPY: "ja-JP", // Japanese Yen
  CNY: "zh-CN", // Chinese Yuan
  RUB: "ru-RU", // Russian Ruble
  BRL: "pt-BR", // Brazilian Real
  MXN: "es-MX", // Mexican Peso
  ARS: "es-AR", // Argentine Peso
  CHF: "de-CH", // Swiss Franc
  SEK: "sv-SE", // Swedish Krona
  NOK: "nb-NO", // Norwegian Krone
  DKK: "da-DK", // Danish Krone
  SGD: "en-SG", // Singapore Dollar
  MYR: "ms-MY", // Malaysian Ringgit
  HKD: "zh-HK", // Hong Kong Dollar
  TWD: "zh-TW", // Taiwan Dollar
  KRW: "ko-KR", // South Korean Won
  TRY: "tr-TR", // Turkish Lira
  PLN: "pl-PL", // Polish Złoty
  THB: "th-TH", // Thai Baht
  IDR: "id-ID", // Indonesian Rupiah
  ILS: "he-IL", // Israeli Shekel
  PHP: "fil-PH", // Philippine Peso
  CZK: "cs-CZ", // Czech Koruna
  HUF: "hu-HU", // Hungarian Forint
  AED: "ar-AE", // United Arab Emirates Dirham
  SAR: "ar-SA", // Saudi Riyal
}

/**
 * i18nPriceFormatter 
 * used for formatting price against shopify's active currency
 * @param {number} price - required - price in cents 
 * @param {{ locale?: string, currency?: string, rate?: number, formatOnly?: boolean}} options 
 */
const DKi18nPriceFormatter = (price, options) => {
  /** @type {number} */
  const rate = options?.rate || window.Shopify?.currency?.rate || 1;

  /** @type {string} */
  const currency = options?.currency || window.Shopify?.currency?.active || "USD";

  const locale = options?.locale || DKCurrencyLocaleMap[currency] || "en-US";

  const priceInMainUnit = price / 100;

  const formatValue = options?.formatOnly ? priceInMainUnit : priceInMainUnit * rate;

  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency,
  }).format(formatValue);
};

/**
 * @typedef {Object} DiscountSettings
 * @property {string} code
 * @property {boolean} active
 * @property {string[]} eligibleTags
 * @property {"domestic" | "international" | "global"} region
 * @property {string} textBeforePrice
 * @property {string} textAfterPrice
 * @property {string} productCardPosition
 * @property {number} percent
 * @returns {{price: number, text: string}}
 */

/** @type {DiscountSettings} */
const discountSettings = getDiscountCodeSettings();

/**
 * @param {number} line_price
 * @param {string[]} tags
 */
const calculatePromoPrice = (line_price, tags) => {
  if (!discountSettings) {
    console.error("Discount settings source code not found");
    return {
      price: null,
      text: null,
    };
  }

  const { 
    active, 
    eligibleTags, 
    region, 
    textBeforePrice, 
    textAfterPrice, 
    percent, 
    showPromoPriceInCart 
  } = discountSettings;

  /** @type {string} */
  const userCountry = window.Shopify.country.toLowerCase()
  const isInternational = userCountry !== "us"

  const regionvalidation =
    (region === "international" && isInternational) ||
    (region === "domestic" && !isInternational) ||
    region === "global";

  if (!active || !regionvalidation)
    return {
      price: null,
      text: null,
    };

  const eligibleTagsSplit = eligibleTags.split(",").map((tag) => tag.trim());

  if (!tags.some((tag) => eligibleTagsSplit.includes(tag)))
    return {
      price: null,
      text: null,
    };

  const calculation = line_price * ((100 - percent) / 100);

  const promoPriceText = DKi18nPriceFormatter(calculation, { formatOnly: true });

  return {
    price: calculation,
    text: `${textBeforePrice} ${showPromoPriceInCart ? promoPriceText : ""} ${textAfterPrice}`,
  };
};

/** 
 * @typedef {Object} CarouselResponseData 
 * @property {string} tags
 * @property {string} price
 * @property {string | undefined} loyalty_new_price
 * @property {string | undefined} loyalty_cashback
*/

let loyalty_product_card_tpl = "";

/**
 * @param {CarouselResponseData} data
 * @returns {CarouselResponseData}
 */
const addLoyaltyDataToProductCard = (data) => {
  /**
   * @typedef {Object} LoyaltySettings
   * @property {boolean} show_in_product_cards
   * @property {"domestic" | "international" | "global"} loyalty_region
   * @property {string} eligible_product_tags
   * @property {number} loyalty_percent_off
   * @property {number} loyalty_cash_back
   * @property {string} loyalty_product_card_text
   * @property {string} stash_text
   */

  if (DK?.hasLoyalty) return data;

  /** @type {LoyaltySettings} */
  const loyaltySettings = JSON.parse(
    document.querySelector("[data-loyalty-settings]").innerHTML || "null"
  );

  if (!loyaltySettings) {
    console.error("Loyalty settings not found");
    return data;
  }

  const {
    show_in_product_cards,
    loyalty_region,
    eligible_product_tags,
    loyalty_percent_off,
    loyalty_cash_back,
    loyalty_product_card_text,
    stash_text
  } = loyaltySettings;

  const eligibleTags = eligible_product_tags.split(",");

  const currentProductTags = data.tags.split(",");

  const isNotEligible = !currentProductTags.some((tag) => eligibleTags.includes(tag.trim()));

  const userCountry = window.Shopify.country.toLowerCase();
  const isInternational = userCountry !== "us";

  const isUserInRegion =
    (loyalty_region === "international" && isInternational) ||
    (loyalty_region === "domestic" && !isInternational) ||
    loyalty_region === "global";

  if (!show_in_product_cards || isNotEligible || !isUserInRegion) return data;

  const isUserInJapan = userCountry === "jp";

  const productPrice = isUserInJapan
    ? parseFloat(data.price)
    : parseFloat(data.price) * 100;

  const valueOfPercentage = productPrice * (loyalty_percent_off / 100);

  const priceAfterDiscount = productPrice - valueOfPercentage;

  const priceAfterDiscountText = DKi18nPriceFormatter(priceAfterDiscount, { formatOnly: true });

  const cashbackCalculation = priceAfterDiscount * (loyalty_cash_back / 100);
  const cashbackText = DKi18nPriceFormatter(cashbackCalculation, { formatOnly: true });

  data.loyalty_cashback = cashbackText;

  const loyaltyText = loyalty_product_card_text
    .replace("|NEW_PRICE|", priceAfterDiscountText)
    .replace("|AMOUNT_CASHBACK|", cashbackText)
    .replace("|STASH_TEXT|", `<span class="loyalty-stash-text tw-font-[med25] tw-font-medium tw-text-[.625rem] tw-inline-block">${stash_text}</span>`);

  data.loyalty_html = `
  <span class="tw-text-[.625rem] tw-text-[var(--loyalty-pricing-text-color)] tw-font-[med20] tw-font-medium tw-m-0">${loyaltyText}</span>
  `

  return data;
};

window.dispatchedEvents = [];

var Hogan = {};

!(function (t) {
  function n(t, n, e) {
    var i;
    return (
      n &&
        'object' == typeof n &&
        (null != n[t] ? (i = n[t]) : e && n.get && 'function' == typeof n.get && (i = n.get(t))),
      i
    );
  }
  (t.Template = function (t, n, e, i) {
    (t = t || {}),
      (this.r = t.code || this.r),
      (this.c = e),
      (this.options = i || {}),
      (this.text = n || ''),
      (this.partials = t.partials || {}),
      (this.subs = t.subs || {}),
      (this.buf = '');
  }),
    (t.Template.prototype = {
      r: function (t, n, e) {
        return '';
      },
      v: function (t) {
        return (
          (t = u(t)),
          o.test(t)
            ? t
                .replace(e, '&amp;')
                .replace(i, '&lt;')
                .replace(r, '&gt;')
                .replace(s, '&#39;')
                .replace(a, '&quot;')
            : t
        );
      },
      t: u,
      render: function (t, n, e) {
        return this.ri([t], n || {}, e);
      },
      ri: function (t, n, e) {
        return this.r(t, n, e);
      },
      ep: function (t, n) {
        var e = this.partials[t],
          i = n[e.name];
        if (e.instance && e.base == i) return e.instance;
        if ('string' == typeof i) {
          if (!this.c) throw new Error('No compiler available.');
          i = this.c.compile(i, this.options);
        }
        if (!i) return null;
        if (((this.partials[t].base = i), e.subs)) {
          n.stackText || (n.stackText = {});
          for (key in e.subs)
            n.stackText[key] ||
              (n.stackText[key] =
                void 0 !== this.activeSub && n.stackText[this.activeSub]
                  ? n.stackText[this.activeSub]
                  : this.text);
          i = (function (t, n, e, i, r, s) {
            function a() {}
            function o() {}
            var u;
            (a.prototype = t), (o.prototype = t.subs);
            var c = new a();
            (c.subs = new o()),
              (c.subsText = {}),
              (c.buf = ''),
              (i = i || {}),
              (c.stackSubs = i),
              (c.subsText = s);
            for (u in n) i[u] || (i[u] = n[u]);
            for (u in i) c.subs[u] = i[u];
            (r = r || {}), (c.stackPartials = r);
            for (u in e) r[u] || (r[u] = e[u]);
            for (u in r) c.partials[u] = r[u];
            return c;
          })(i, e.subs, e.partials, this.stackSubs, this.stackPartials, n.stackText);
        }
        return (this.partials[t].instance = i), i;
      },
      rp: function (t, n, e, i) {
        var r = this.ep(t, e);
        return r ? r.ri(n, e, i) : '';
      },
      rs: function (t, n, e) {
        var i = t[t.length - 1];
        if (c(i)) for (var r = 0; r < i.length; r++) t.push(i[r]), e(t, n, this), t.pop();
        else e(t, n, this);
      },
      s: function (t, n, e, i, r, s, a) {
        var o;
        return (
          (!c(t) || 0 !== t.length) &&
          ('function' == typeof t && (t = this.ms(t, n, e, i, r, s, a)),
          (o = !!t),
          !i && o && n && n.push('object' == typeof t ? t : n[n.length - 1]),
          o)
        );
      },
      d: function (t, e, i, r) {
        var s,
          a = t.split('.'),
          o = this.f(a[0], e, i, r),
          u = this.options.modelGet,
          l = null;
        if ('.' === t && c(e[e.length - 2])) o = e[e.length - 1];
        else
          for (var f = 1; f < a.length; f++)
            null != (s = n(a[f], o, u)) ? ((l = o), (o = s)) : (o = '');
        return (
          !(r && !o) &&
          (r || 'function' != typeof o || (e.push(l), (o = this.mv(o, e, i)), e.pop()), o)
        );
      },
      f: function (t, e, i, r) {
        for (var s = !1, a = !1, o = this.options.modelGet, u = e.length - 1; u >= 0; u--)
          if (null != (s = n(t, e[u], o))) {
            a = !0;
            break;
          }
        return a ? (r || 'function' != typeof s || (s = this.mv(s, e, i)), s) : !r && '';
      },
      ls: function (t, n, e, i, r) {
        var s = this.options.delimiters;
        return (
          (this.options.delimiters = r),
          this.b(this.ct(u(t.call(n, i)), n, e)),
          (this.options.delimiters = s),
          !1
        );
      },
      ct: function (t, n, e) {
        if (this.options.disableLambda) throw new Error('Lambda features disabled.');
        return this.c.compile(t, this.options).render(n, e);
      },
      b: function (t) {
        this.buf += t;
      },
      fl: function () {
        var t = this.buf;
        return (this.buf = ''), t;
      },
      ms: function (t, n, e, i, r, s, a) {
        var o,
          u = n[n.length - 1],
          c = t.call(u);
        return 'function' == typeof c
          ? !!i ||
              ((o =
                this.activeSub && this.subsText && this.subsText[this.activeSub]
                  ? this.subsText[this.activeSub]
                  : this.text),
              this.ls(c, u, e, o.substring(r, s), a))
          : c;
      },
      mv: function (t, n, e) {
        var i = n[n.length - 1],
          r = t.call(i);
        return 'function' == typeof r ? this.ct(u(r.call(i)), i, e) : r;
      },
      sub: function (t, n, e, i) {
        var r = this.subs[t];
        r && ((this.activeSub = t), r(n, e, this, i), (this.activeSub = !1));
      },
    });
  var e = /&/g,
    i = /</g,
    r = />/g,
    s = /\'/g,
    a = /\"/g,
    o = /[&<>\"\']/;
  function u(t) {
    return String(null === t || void 0 === t ? '' : t);
  }
  var c =
    Array.isArray ||
    function (t) {
      return '[object Array]' === Object.prototype.toString.call(t);
    };
})('undefined' != typeof exports ? exports : Hogan),
  (function (t) {
    var n = /\S/,
      e = /\"/g,
      i = /\n/g,
      r = /\r/g,
      s = /\\/g;
    function a(t) {
      '}' === t.n.substr(t.n.length - 1) && (t.n = t.n.substring(0, t.n.length - 1));
    }
    function o(t) {
      return t.trim ? t.trim() : t.replace(/^\s*|\s*$/g, '');
    }
    function u(t, n, e) {
      if (n.charAt(e) != t.charAt(0)) return !1;
      for (var i = 1, r = t.length; i < r; i++) if (n.charAt(e + i) != t.charAt(i)) return !1;
      return !0;
    }
    (t.tags = {
      '#': 1,
      '^': 2,
      '<': 3,
      $: 4,
      '/': 5,
      '!': 6,
      '>': 7,
      '=': 8,
      _v: 9,
      '{': 10,
      '&': 11,
      _t: 12,
    }),
      (t.scan = function (e, i) {
        var r,
          s,
          c,
          l,
          f,
          h = e.length,
          p = 0,
          g = null,
          v = null,
          b = '',
          d = [],
          m = !1,
          x = 0,
          w = 0,
          k = '{{',
          y = '}}';
        function T() {
          b.length > 0 && (d.push({ tag: '_t', text: new String(b) }), (b = ''));
        }
        function S(e, i) {
          if (
            (T(),
            e &&
              (function () {
                for (var e = !0, i = w; i < d.length; i++)
                  if (
                    !(e =
                      t.tags[d[i].tag] < t.tags._v ||
                      ('_t' == d[i].tag && null === d[i].text.match(n)))
                  )
                    return !1;
                return e;
              })())
          )
            for (var r, s = w; s < d.length; s++)
              d[s].text &&
                ((r = d[s + 1]) && '>' == r.tag && (r.indent = d[s].text.toString()),
                d.splice(s, 1));
          else i || d.push({ tag: '\n' });
          (m = !1), (w = d.length);
        }
        for (i && ((i = i.split(' ')), (k = i[0]), (y = i[1])), x = 0; x < h; x++)
          0 == p
            ? u(k, e, x)
              ? (--x, T(), (p = 1))
              : '\n' == e.charAt(x)
              ? S(m)
              : (b += e.charAt(x))
            : 1 == p
            ? ((x += k.length - 1),
              '=' == (g = (v = t.tags[e.charAt(x + 1)]) ? e.charAt(x + 1) : '_v')
                ? ((s = x),
                  void 0,
                  void 0,
                  void 0,
                  (c = '=' + y),
                  (l = (r = e).indexOf(c, s)),
                  (f = o(r.substring(r.indexOf('=', s) + 1, l)).split(' ')),
                  (k = f[0]),
                  (y = f[f.length - 1]),
                  (x = l + c.length - 1),
                  (p = 0))
                : (v && x++, (p = 2)),
              (m = x))
            : u(y, e, x)
            ? (d.push({
                tag: g,
                n: o(b),
                otag: k,
                ctag: y,
                i: '/' == g ? m - k.length : x + y.length,
              }),
              (b = ''),
              (x += y.length - 1),
              (p = 0),
              '{' == g && ('}}' == y ? x++ : a(d[d.length - 1])))
            : (b += e.charAt(x));
        return S(m, !0), d;
      });
    var c = { _t: !0, '\n': !0, $: !0, '/': !0 };
    function l(t, n) {
      for (var e = 0, i = n.length; e < i; e++) if (n[e].o == t.n) return (t.tag = '#'), !0;
    }
    function f(t, n, e) {
      for (var i = 0, r = e.length; i < r; i++) if (e[i].c == t && e[i].o == n) return !0;
    }
    function h(t) {
      var n = [];
      for (var e in t.partials)
        n.push('"' + g(e) + '":{name:"' + g(t.partials[e].name) + '", ' + h(t.partials[e]) + '}');
      return (
        'partials: {' +
        n.join(',') +
        '}, subs: ' +
        (function (t) {
          var n = [];
          for (var e in t) n.push('"' + g(e) + '": function(c,p,t,i) {' + t[e] + '}');
          return '{ ' + n.join(',') + ' }';
        })(t.subs)
      );
    }
    t.stringify = function (n, e, i) {
      return '{code: function (c,p,i) { ' + t.wrapMain(n.code) + ' },' + h(n) + '}';
    };
    var p = 0;
    function g(t) {
      return t.replace(s, '\\\\').replace(e, '\\"').replace(i, '\\n').replace(r, '\\r');
    }
    function v(t) {
      return ~t.indexOf('.') ? 'd' : 'f';
    }
    function b(t, n) {
      var e = '<' + (n.prefix || '') + t.n + p++;
      return (
        (n.partials[e] = { name: t.n, partials: {} }),
        (n.code += 't.b(t.rp("' + g(e) + '",c,p,"' + (t.indent || '') + '"));'),
        e
      );
    }
    function d(t, n) {
      n.code += 't.b(t.t(t.' + v(t.n) + '("' + g(t.n) + '",c,p,0)));';
    }
    function m(t) {
      return 't.b(' + t + ');';
    }
    (t.generate = function (n, e, i) {
      p = 0;
      var r = { code: '', subs: {}, partials: {} };
      return t.walk(n, r), i.asString ? this.stringify(r, e, i) : this.makeTemplate(r, e, i);
    }),
      (t.wrapMain = function (t) {
        return 'var t=this;t.b(i=i||"");' + t + 'return t.fl();';
      }),
      (t.template = t.Template),
      (t.makeTemplate = function (t, n, e) {
        var i = this.makePartials(t);
        return (
          (i.code = new Function('c', 'p', 'i', this.wrapMain(t.code))),
          new this.template(i, n, this, e)
        );
      }),
      (t.makePartials = function (t) {
        var n,
          e = { subs: {}, partials: t.partials, name: t.name };
        for (n in e.partials) e.partials[n] = this.makePartials(e.partials[n]);
        for (n in t.subs) e.subs[n] = new Function('c', 'p', 't', 'i', t.subs[n]);
        return e;
      }),
      (t.codegen = {
        '#': function (n, e) {
          (e.code +=
            'if(t.s(t.' +
            v(n.n) +
            '("' +
            g(n.n) +
            '",c,p,1),c,p,0,' +
            n.i +
            ',' +
            n.end +
            ',"' +
            n.otag +
            ' ' +
            n.ctag +
            '")){t.rs(c,p,function(c,p,t){'),
            t.walk(n.nodes, e),
            (e.code += '});c.pop();}');
        },
        '^': function (n, e) {
          (e.code += 'if(!t.s(t.' + v(n.n) + '("' + g(n.n) + '",c,p,1),c,p,1,0,0,"")){'),
            t.walk(n.nodes, e),
            (e.code += '};');
        },
        '>': b,
        '<': function (n, e) {
          var i = { partials: {}, code: '', subs: {}, inPartial: !0 };
          t.walk(n.nodes, i);
          var r = e.partials[b(n, e)];
          (r.subs = i.subs), (r.partials = i.partials);
        },
        $: function (n, e) {
          var i = { subs: {}, code: '', partials: e.partials, prefix: n.n };
          t.walk(n.nodes, i),
            (e.subs[n.n] = i.code),
            e.inPartial || (e.code += 't.sub("' + g(n.n) + '",c,p,i);');
        },
        '\n': function (t, n) {
          n.code += m('"\\n"' + (t.last ? '' : ' + i'));
        },
        _v: function (t, n) {
          n.code += 't.b(t.v(t.' + v(t.n) + '("' + g(t.n) + '",c,p,0)));';
        },
        _t: function (t, n) {
          n.code += m('"' + g(t.text) + '"');
        },
        '{': d,
        '&': d,
      }),
      (t.walk = function (n, e) {
        for (var i, r = 0, s = n.length; r < s; r++) (i = t.codegen[n[r].tag]) && i(n[r], e);
        return e;
      }),
      (t.parse = function (n, e, i) {
        return (function n(e, i, r, s) {
          var a,
            o = [],
            u = null,
            h = null;
          for (a = r[r.length - 1]; e.length > 0; ) {
            if (((h = e.shift()), a && '<' == a.tag && !(h.tag in c)))
              throw new Error('Illegal content in < super tag.');
            if (t.tags[h.tag] <= t.tags.$ || l(h, s)) r.push(h), (h.nodes = n(e, h.tag, r, s));
            else {
              if ('/' == h.tag) {
                if (0 === r.length) throw new Error('Closing tag without opener: /' + h.n);
                if (((u = r.pop()), h.n != u.n && !f(h.n, u.n, s)))
                  throw new Error('Nesting error: ' + u.n + ' vs. ' + h.n);
                return (u.end = h.i), o;
              }
              '\n' == h.tag && (h.last = 0 == e.length || '\n' == e[0].tag);
            }
            o.push(h);
          }
          if (r.length > 0) throw new Error('missing closing tag: ' + r.pop().n);
          return o;
        })(n, 0, [], (i = i || {}).sectionTags || []);
      }),
      (t.cache = {}),
      (t.cacheKey = function (t, n) {
        return [t, !!n.asString, !!n.disableLambda, n.delimiters, !!n.modelGet].join('||');
      }),
      (t.compile = function (n, e) {
        e = e || {};
        var i = t.cacheKey(n, e),
          r = this.cache[i];
        if (r) {
          var s = r.partials;
          for (var a in s) delete s[a].instance;
          return r;
        }
        return (
          (r = this.generate(this.parse(this.scan(n, e.delimiters), n, e), n, e)),
          (this.cache[i] = r)
        );
      });
  })('undefined' != typeof exports ? exports : Hogan);

if (typeof DK == 'undefined') DK = {};

DK.clearStore = function () {
  for (var i in localStorage) {
    if (typeof i == 'string') {
      if (i.substring(0, 4) == '_fe_') {
        localStorage.removeItem(i);
      }
    }
  }
};

function formatPrice(price) {
  return (price / 100).toFixed(2);
}

DK.renderRecentlyvieweditems = function (product_id) {
  var uri = '/recentlyvieweditems?';
  uri += 'c=' + Shopify.currency.active + '|' + Shopify.currency.rate + '|' + Shopify.country;
  uri += '&r=' + DK.get('recently_viewed_product');
  uri += '&p=' + product_id;

  DK.api('GET', uri, function (response) {
    if (response.data.length > 0) {
      for (var i in response.data) {
        cMatrix[response.data[i].id] = [];
        for (var s in response.data[i].sizes) {
          cMatrix[response.data[i].id].push({
            title: response.data[i].sizes[s].size,
            id: response.data[i].sizes[s].variant_id,
            sku: response.data[i].sizes[s].sku,
            quantity: response.data[i].sizes[s].inventory,
            parentTags: response.data[i].tags || "",
          });
        }
      }
      var tpl =
        '{{#data}}<product-card data-test-id="product-card" data-id="{{id}}" data-handle="{{handle}}" class="product-card render-recent" data-compare-at-price="{{compare_at_price}}" data-price="{{price}}"{{#meta}} data-preorder="{{meta.products.preorder}}" data-category="{{meta.products.category}}" data-subcategory="{{meta.products.subcategory}}" data-microcategory="{{meta.products.microcategory}}" data-mpid="{{meta.products.parentId}}"{{/meta}}> <div class="card-actions"><div class="card-actions-sale" data-pid="{{id}}"></div><swym-wishlist-btn class="add-to-wishlist-btn tw-absolute tw-right-0" data-wish-action="addToWishlist" data-product-id="{{id}}" data-product-handle="{{handle}}"></swym-wishlist-btn></div><div class="card-content"> <div class="image-holder loading"> <a href="/products/{{handle}}"> <img loading="lazy" fetchpriority="low" data-id="{{id}}" data-card-status=0 data-src="{{image}}" alt="" class="grid-image"> <img src="{{image2}}" alt="" class="grid-image hover" data-quickview-container></a> </div><div class="content-holder"><div style="display:flex;justify-content:space-between;"><p class="product-title"><a href="/products/{{handle}}">{{title}}</a></p><p class="product-price desktop">{{#strike}}<span class="strike">{{compare_at_price_text}}</span>&nbsp; {{/strike}}{{price_text}}</p></div><p class="vendor-name">{{#vendor}}{{vendor}}{{/vendor}}</p><p class="product-price mobile">{{#strike}}<span class="strike">{{compare_at_price_text}}</span>&nbsp; {{/strike}}{{price_text}}</p></div></div></product-card>{{/data}}';
      var H = Hogan.compile(tpl);
      var xhtml = H.render(response);
      $('#recentlyvieweditems').html(xhtml);
      sliceCollection();
    } else {
      $('#recently-viewed-items').html('');
    }
  });
};

DK.sliceCollection = function (obj, force) {
  if (typeof force == 'number') {
    slicecount = force;
  } else {
    slicecount = 4;
    if ($(window).width() <= 1215) slicecount = 4;
    if ($(window).width() <= 950) slicecount = 3;
    if ($(window).width() <= 600) slicecount = 2;
  }
  snapCollection(obj, 'slice', slicecount);
};

DK.searchTiming = false;
DK.openSearch = function (bool) {
  if (typeof bool == 'boolean' && !bool) {
    $('#search-box-area').hide();
    $('#searchoverlay').hide();
    return;
  }
  $('#search-box-area').slideDown();
  $('#searchoverlay').show();
  var so = $('#searchinput');
  so.focus();
};

DK.subscribeEmail = function (email, location, x) {
  DK.api(
    'POST',
    '/iterablex/_subscribe',
    { email: email, path: window.location.pathname, location: location },
    function (r) {
      if (typeof x == 'function') {
        x(r);
      }
    }
  );
};

DK.lightUp = function (count) {
  $("#wishlist-link img").attr(
    "src",
    "https://cdn.shopify.com/s/files/1/0634/6335/8721/files/heart_filled.svg?v=1697670063"
  );
  var dd = '10px';
  if (count >= 10) dd = '8px';
  if (count > 99) {
    dd = '2px';
    count = '99+';
  }
  $('#wish-count').html(count).css('right', dd).show();
};

DK.uuid = function () {
  return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
    (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16)
  );
};

DK.whoami = function () {
  var test = DK.get('whoami');
  if (!test) {
    test = DK.uuid();
    DK.set('whoami', test);
  }
  return test;
};

DK.isMobile = $(window).width() <= 900 ? true : false;

DK.shorthand = [
  'xx-small/x-small|xxs/xs',
  'xx-small|xxs',
  'xx-large|xxl',
  'x-small/small|xs/s',
  'x-small|xs',
  'x-large/xx-large|xl/xxl',
  'x-large|xl',
  'us 9 1/2|us 9.5',
  'us 8 1/2|us 8.5',
  'us 7 1/2|us 7.5',
  'us 6 1/2|us 6.5',
  'us 5 1/2|us 5.5',
  'small/medium|s/m',
  'small|s',
  'one size|os',
  'medium/large|m/l',
  'medium|m',
  'large/x-large|l/xl',
  'large|l',
];

DK.findShortHand = function (input) {
  for (var i in DK.shorthand) {
    var split = DK.shorthand[i].split('|');
    if (split[0] == input.toLowerCase()) {
      return split[1];
    }
  }
  return false;
};
DK.get = function (key) {
  key = '_fe_' + key;
  test = localStorage.getItem(key);

  timeStamp = Math.round(new Date().getTime() / 1000);
  if (test != null) {
    test = $.parseJSON(test);
    if (typeof test?.expiry == 'number' && timeStamp > test.expiry) {
      localStorage.removeItem(key);
      return false;
    }
    return test?.data || '';
  } else return false;
};

DK.set = function (key, val, expiry) {
  key = '_fe_' + key;
  obj = {};
  obj.data = val;
  if (typeof expiry == 'number') {
    obj.expiry = Math.round(new Date().getTime() / 1000) + 60 * 60 * expiry;
  }
  obj = JSON.stringify(obj);

  if (DK.isLocalStorageFull()) {
    DK.clearStore();
  }

  localStorage.setItem(key, obj);
};

DK.email = DK.get('em');

DK.init = function () {
  DK.mobileNavToggle();
  DK.mount();
};

DK.ntimer = false;
DK.showNavigation = function (bool) {
  if (typeof DK.ntimer == 'number') {
    window.clearTimeout(DK.ntimer);
    DK.ntimer = false;
  }

  var ng = $('#header-expanded');

  if (bool) {
    DK.ntimer = window.setTimeout(function () {
      ng.show();
    }, 150);
  } else {
    DK.ntimer = window.setTimeout(function () {
      ng.hide();

      $('li.main-links-hovered').removeClass('main-links-hovered');
    }, 150);
  }
};

DK.mobileNavToggle = function () {
  var burger = $('.mob-menu');
  if (burger.length == 0) {
    window.setTimeout(function () {
      DK.mobileNavToggle();
    }, 100);
  } else {
    burger.click(function () {
      $('.mobile-nav-container').addClass('nav-slide');
      $('.mobile-back').addClass('hide-opacity');
      $('.main-links').show();
      DK.modalShadowShow();

      $('.mobile-nav-container').append($('.rb-menu'));
      $('.rb-menu').show();

      // $("#account-link").click(function () {
      //   $(".mobile-nav-container").removeClass("nav-slide");
      //   DK.modalShadowHide();
      //   initLogin();
      // });

      $('li a').click(function (e) {
        e.stopPropagation();
        return;
      });

      $('li').click(function () {
        var liName = $(this).text();

        liName = liName.split(' <');
        liName = liName[0];
        if (!$(this).hasClass('col')) {
          $('.sub-group').hide();
          $('.main-links').hide();
          $('.mobile-nav-container').append($('#header-expanded'));
          var id = $(this).attr('data-id');
          $('.sub-group[data-id=' + id + ']').show();
          if (!$('.sub-group[data-id=' + id + ']').hasClass('mobile-title'))
            $('.sub-group[data-id=' + id + ']')
              .prepend('<span class="liName">' + liName + '</span>')
              .addClass('mobile-title');

          if (id == 9) {
            $('.sub-group[data-id=' + id + '] .col ul').show();
          }

          $('#header-expanded').show();
          $('.mobile-back').removeClass('hide-opacity');
          $('.rb-menu').hide();

          $('.mobile-back').click(function () {
            $('.sub-group').show();
            $('.main-links').show();
            $('.sub-group[data-id=' + id + ']').hide();
            $('#header-expanded').hide();
            $('.mobile-back').addClass('hide-opacity');
            $('.rb-menu').show();
          });

          $.each($('.sub-group[data-id=' + id + '] h3'), function () {
            if (!$(this).hasClass('mobiled') && !$(this).hasClass('separate-category-groups')) {
              var html = $(this).html();
              html +=
                '<span style="float:right;margin-right:20px;"><i class="fa-solid fa-angle-down"></i></span>';
              $(this).html(html).addClass('mobiled');

              $('h3').click(function (e) {
                e.stopImmediatePropagation();
                if (!$(this).hasClass('separate-category-groups')) {
                  e.preventDefault();
                  var targetgroup = $(this).next('ul');
                  if (targetgroup.hasClass('opened')) {
                    targetgroup.removeClass('opened').slideUp();
                    $('.openedh3 i').removeClass('fa-angle-up').addClass('fa-angle-down');
                    $(this).removeClass('openedh3');
                  } else {
                    targetgroup.addClass('opened').slideDown();
                    $(this).addClass('openedh3');
                    $('.openedh3 i').removeClass('fa-angle-down').addClass('fa-angle-up');
                  }
                }
              });
            }
          });
        }
      });
    });
    $('.mobile-closer').click(function () {
      $('.mobile-nav-container').removeClass('nav-slide');
      DK.modalShadowHide();
    });
    $('#overlay').click(function () {
      $('.mobile-nav-container').removeClass('nav-slide');
      DK.modalShadowHide();
    });
  }
};

DK.modalShadowShow = function () {
  $('#overlay').show();
  // $('html body').css('overflow','hidden');
};

DK.modalShadowHide = function () {
  $('#overlay').hide();
  $('html body').css('overflow', '');
  DK.darkboxClose();
  $('.wbox').hide();
};

DK.darkboxShow = function (forced) {
  if (typeof forced == 'undefined') $('#darkbox').attr('style', '');
  DK.modalShadowShow();
  $('#darkbox').show();
  var dbw = $('#darkbox').get()[0].scrollWidth;
  var left = $(window).width() / 2 - dbw / 2;
  $('#darkbox').css('left', left);

  $('#darkbox-header-closer').click(function () {
    DK.modalShadowHide();
  });
};

DK.darkboxClose = function () {
  $('#darkbox').hide();
};

DK.darkboxContent = function (content, styling) {
  if (typeof styling == 'object') {
    $.each(styling, function (k, v) {
      $('#darkbox').css(k, v);
    });
    DK.darkboxShow(true);
  } else DK.darkboxShow();

  if (typeof content == 'object') {
    $('#darkbox-header-text').html(content.header);
    $('#darkbox-content').html(content.content);
  }

  if (typeof content == 'string') {
    $('#darkbox-content').html(content);
  }
};

DK.api_server = 'https://xl.dollsmktg.com/api';

DK.api = function (method, uri, data, callback) {
  if (typeof data == 'function') {
    callback = data;
  }

  var pkg = {
    type: method,
    url: this.api_server + uri,
    success: function (response) {
      if (typeof response.errors == 'object') {
        response.error_message = response.errors.join('<br />');
      }

      if (
        typeof response.error_message == 'string' &&
        response.error_message.indexOf('Missing permission') != -1
      ) {
      }
      callback(response);
    },
  };
  pkg.headers = {};
  if (typeof data == 'object' && (method == 'POST' || method == 'PATCH'))
    pkg.data = JSON.stringify(data);
  if (typeof data == 'string') {
    pkg.headers['Accept'] = 'json';
  }
  $.ajax(pkg);
};

DK.getQuery = function (name) {
  var match = RegExp('[?&]' + name + '=([^&]*)').exec(window.location.search);
  return match && decodeURIComponent(match[1].replace(/\+/g, ' '));
};

DK.addToWishlist = function (pid, bool, target) {
  if (DK.customer) {
    var uri = '/wishlists/_add';
    if (!bool) uri = '/wishlists/_remove';
    target = $('img', target);
    if (typeof DK.qcontext == 'boolean' && !DK.qcontext) {
      DK.qcontext = DK.productData;
    } else if (typeof DK.qcontext != 'object') DK.qcontext = {};

    DK.api(
      'POST',
      uri,
      {
        product_id: pid,
        email: DK.ident,
        aid: DK.aid,
        hm: DK.apiKey,
        context: DK.qcontext,
      },
      function (r) {
        if (r.status == 1) {
          DK.set('wishlist', r.data);

          if (bool) {
            target
              .addClass('wicon')
              .attr(
                "src",
                "https://cdn.shopify.com/s/files/1/0634/6335/8721/files/heart_filled.svg?v=1697670063"
              );
          } else {
            target
              .removeClass('wicon')
              .attr(
                "src",
                "https://cdn.shopify.com/s/files/1/0634/6335/8721/files/heart_empty.svg?v=1697670062"
              );
          }
        }
      }
    );
  }
};

DK.recommendationQuickLoad = {};
DK.renderRecommendations = function (xid) {
  var uri = '/recommendations/rel_' + xid + '?';

  var r = DK.get('recently_viewed_product');
  if (typeof r == 'object') {
    r = r.join(',');
  } else {
    r = '';
  }

  uri += 'c=' + Shopify.currency.active + '|' + Shopify.currency.rate + '|' + Shopify.country;
  uri += '&r=' + r;

  DK.api('GET', uri, function (response) {
    window.dkProductPageExpectedCarousels = Object.keys(response).filter((key) => {
      if (key === "data2" || key === "data3") return response[key].length > 0;

      return false;
    }).length;

    if (DK.getQuery('pwtx')) {
      DK.set('pwt', false);
    }
    if (DK.getQuery('pwt')) {
      DK.set('pwt', 1);
    }
    var pwt = DK.get('pwt');
    pwt = true;

    if (!pwt) {
      var tpl =
        '{{#data}}<product-card data-test-id="product-card" data-id="{{id}}" data-handle="{{handle}}" class="product-card render-recs" data-compare-at-price="{{compare_at_price}}" data-price="{{price}}"{{#meta}} data-path="{{meta.products.path}}" data-preorder="{{meta.products.preorder}}" data-category="{{meta.products.category}}" data-subcategory="{{meta.products.subcategory}}" data-microcategory="{{meta.products.microcategory}}" data-mpid="{{meta.products.parentId}}"{{/meta}}> <div class="card-actions"><div class="card-actions-sale" data-pid="{{id}}"></div><swym-wishlist-btn class="add-to-wishlist-btn tw-absolute tw-right-0 tw-p-[11px]" data-wish-action="addToWishlist" data-product-id="{{id}}" data-product-handle="{{handle}}"></swym-wishlist-btn></div><div class="card-content"> <div class="image-holder loading" data-quickview-container> <a href="/products/{{handle}}"> <img loading="lazy" fetchpriority="low" data-id="{{id}}" data-card-status=0 data-src="{{image}}" alt="" class="grid-image" {{#image2}}data-src2="{{image2}}"{{/image2}}> </a></div><div class="content-holder"><div style="display:flex;justify-content:space-between;"><p class="product-title"><a href="/products/{{handle}}">{{title}}</a></p><p class="product-price desktop">{{#strike}}<span class="strike">{{compare_at_price_text}}</span>&nbsp; {{/strike}}{{price_text}}</p></div> <p class="vendor-name">{{#vendor}}{{vendor}}{{/vendor}}</p><p class="product-price mobile">{{#strike}}<span class="strike">{{compare_at_price_text}}</span>&nbsp; {{/strike}}{{price_text}}</p></div></div></product-card>{{/data}}';
      
      var H = Hogan.compile(tpl);
      for (var i in response.data) {
        cMatrix[response.data[i].id] = [];
        for (var s in response.data[i].sizes) {
          cMatrix[response.data[i].id].push({
            title: response.data[i].sizes[s].size,
            id: response.data[i].sizes[s].variant_id,
            sku: response.data[i].sizes[s].sku,
            quantity: response.data[i].sizes[s].inventory,
            parentTags: response.data[i].tags || "",
          });
        }
      }
      $.each(response.data, function (k, v) {
        if (!v.image2) {
          v.image2 = v.image;
        }
      });
      var xhtml = H.render(response);
      $('#recommendations').html(xhtml);
      $('#related-items').show();
      DK.sliceCollection($('#recommendations'));
      var target = $('#recommendations');

      $('img[data-src2]', target).mouseover(function () {
        var src2 = $(this).attr('data-src2');
        $(this).attr('src', src2);
      });
      $('img[data-src2]', target).mouseout(function () {
        var src = $(this).attr('data-src');
        $(this).attr('src', src);
      });
      
    } else {
      /*if (response.data2.length > 0) {
          DK.renderCarousel($('#pair-with-items'),{},response.data2);
        
            $('#pair-with').show();
          return;
          var tpl = '{{#data}}<div data-id="{{id}}" class="product-card" data-compare-at-price="{{compare_at_price}}" data-price="{{price}}"{{#meta}} data-preorder="{{meta.products.preorder}}" data-category="{{meta.products.category}}" data-subcategory="{{meta.products.subcategory}}" data-microcategory="{{meta.products.microcategory}}" data-mpid="{{meta.products.parentId}}"{{/meta}}> <div class="card-actions"><div class="card-actions-sale" data-pid="{{id}}"></div><div class="card-actions-fav" data-pid="{{id}}"><img src="https://cdn.shopify.com/s/files/1/0634/6335/8721/files/heart_empty.svg?v=1697670062"/></div></div><div class="card-content"> <div class="image-holder loading"> <a href="/products/{{handle}}"> <img data-id="{{id}}" data-card-status=0 data-src="{{image}}" alt="" class="grid-image"> <img src="{{image2}}" alt="" class="grid-image hover"></a> <div class="qatc" data-id="{{id}}"><span class="prompt">Add To Bag</span><div class="qatc-options" data-id="{{id}}"></div></div></div><div class="content-holder"> <p class="vendor-name">{{#vendor}}{{vendor}}{{/vendor}}</p><p class="product-title"><a href="/products/{{handle}}">{{title}}</a></p><p class="product-price  ">{{#strike}}<span class="strike">{{currency}}{{compare_at_price}}</span>&nbsp; {{/strike}}{{currency}}{{price}}</p></div></div></div>{{/data}}';
          var H = Hogan.compile(tpl);
          for (var i in response.data2) {
              cMatrix[response.data2[i].id] = [];
              for( var s in response.data2[i].sizes){
                cMatrix[response.data2[i].id].push({title:response.data2[i].sizes[s].size, id:response.data2[i].sizes[s].variant_id, sku: response.data2[i].sizes[s].sku, quantity: response.data2[i].sizes[s].inventory}); 
              }
              
            }
            
        
            $.each(response.data2, function(k,v) {
              if (!v.image2) {
                v.image2 = v.image;
              }     
            });

            var presponse = {};
        	presponse.data = response.data2;
          	var xhtml2 = H.render(presponse);
            $('#pair-with-items').html(xhtml2);
      
             DK.sliceCollection($('#pair-with-items'));
            setupSlide($('#pair-with-items'));
        }*/

      DK.newProdPage = 1;

      const dispatchCarouselEvent = (carouselName = "carousel") => {
        const event = new CustomEvent("carouselLoaded", {
          detail: carouselName,
        })

        window.dispatchedEvents.push(event);

        window.dispatchEvent(event)
      }

      if (typeof response.data2 == 'object') {
        if (response.data2.length > 0) {
          DK.writeMatrix(response.data2);
          DK.recommendationQuickLoad[xid] = response.data2;
          DK.renderCarousel($('#pair-with-items'), {}, response.data2, dispatchCarouselEvent('pair-with-items'));
          $('#pair-with').show();
          $('#pair-with-items').addClass('scroller').addClass('sliding').addClass('slider-pw');
        }
      }

      if (typeof response.data3 == 'object') {
        if (response.data3.length > 0) {
          DK.writeMatrix(response.data3);
          DK.renderCarousel($('#r-viewed-items'), {}, response.data3, dispatchCarouselEvent('recently viewed'));
          $('#r-viewed').show();
          $('#r-viewed-items').addClass('scroller').addClass('sliding').addClass('slider-pw');
        }
      }

      if (typeof response.data == 'object') {
        if (response.data.length > 0) {
          DK.writeMatrix(response.data);
          DK.renderCarousel($('#recommendations'), {}, response.data);
          $('#related-items').show();
          $('#recommendations').addClass('scroller').addClass('sliding').addClass('slider-pw');
        }
      }

      if (typeof response.data4 == 'object') {
        if (response.data4.length > 0) {
          DK.writeMatrix(response.data4);
          DK.renderCarousel($('#t-viewed-items'), {}, response.data4);
          $('#t-viewed').show();
          $('#t-viewed-items').addClass('scroller').addClass('sliding').addClass('slider-pw');
        }
      }
    }
  });
};
var wMatrix = {};
DK.writeMatrix = function (obj) {
  for (var i in obj) {
    var wKey = 'w' + obj[i].id;
    wMatrix[wKey] = [];

    for (var s in obj[i].sizes) {
      wMatrix[wKey].push({
        title: obj[i].sizes[s].size,
        id: obj[i].sizes[s].variant_id,
        sku: obj[i].sizes[s].sku,
        quantity: obj[i].sizes[s].inventory,
      });
    }
  }
};
DK.renderCarousel = function (id, style, data, callback) {
  if (typeof style != 'string') style = 'collection';

  const promoPricingTPL = `
    <div class="tw-truncate tw-text-white">
    {{#promo_price_text}}
      <span class="cart-promo-price tw-text-[var(--promo-code-color)] tw-bg-[var(--promo-code-background-color)] tw-w-fit tw-truncate">
        {{promo_price_text}}
      </span>
    {{/promo_price_text}}
    {{^promo_price_text}}
    {{{loyalty_html}}}
    {{/promo_price_text}}
    </div>
  `;

  var tpl =
  `
    {{#data}}
    <product-card 
      data-test-id="product-card" 
      data-id="{{id}}" 
      data-mpid="{{meta.products.parentId}}" 
      class="product-card render-car" 
      data-clearance="{{clearance}}" 
      data-compare-at-price="{{compare_at_price}}" 
      data-price="{{price}}" 
      data-handle="{{handle}}">
      <div class="card-actions">
        <div 
          class="card-actions-sale" 
          data-pid="{{id}}">
        </div>
        <swym-wishlist-btn 
          class="add-to-wishlist-btn tw-absolute tw-right-0 tw-p-[11px]" 
          data-wish-action="addToWishlist" 
          data-product-id="{{id}}" 
          data-product-handle="{{handle}}">
        </swym-wishlist-btn>
      </div>
      <div class="card-content">
        <div 
          class="image-holder loading" 
          data-quickview-container> 
          <a href="/products/{{handle}}"> 
            <img 
              loading="lazy" 
              fetchpriority="low" 
              data-id="{{id}}" 
              data-card-status=0 
              data-src="{{image}}" 
              alt="" 
              class="grid-image" 
              data-src2="{{image2}}"/>
          </a>
        </div>
        <div class="content-holder">
          <div class="tw-flex tw-justify-between tw-gap-2">
          <div class="tw-flex tw-flex-col tw-min-w-0">
            <div class="tw-flex">
              <a 
                class="product-title tw-uppercase tw-font-medium tw-font-[med20]" 
                href="/products/{{handle}}">
                {{title}}
              </a>
              <product-swatch-info 
                data-color="" 
                class="tw-hidden">
                <script 
                  data-product-json 
                  data-source="dk" 
                  type="application/json">
                    {{{JSON}}}
                </script>
                <div class="tw-flex tw-gap-1">
                  <div data-swatch class="tw-h-[10px] tw-w-[10px] tw-outline tw-outline-white tw-outline-1">
                  </div>
                  <span 
                    data-remaining-colors 
                    class="tw-m-0 tw-text-white tw-text-[10px] tw-relative tw-top-[-2px]">
                  </span>
                </div>
              </product-swatch-info>
            </div>
            <div class="tw-flex tw-flex-col sm:tw-flex-row tw-justify-between tw-flex-wrap sm:tw-items-center">
              {{#vendor}}
              <p class="vendor-name tw-mb-1 sm:tw-mb-0">
                {{vendor}}
              </p>
              {{/vendor}}
            </div>
          </div>
            <div class="product-price desktop tw-text-right">
              {{#strike}}
              <span class="strike">
                {{compare_at_price_text}}
              </span>
              {{/strike}}
              <span>
              {{price_text}}
              </span>
              {{#promoPricingIsRight}}
              <span class="tw-block tw-text-right tw-mt-1">
                ${promoPricingTPL}
              </span>
              {{/promoPricingIsRight}}
            </div>
          </div>
          <div class="tw-flex tw-flex-col  tw-justify-between tw-flex-wrap">
          <p class="product-price mobile">
            {{#strike}}
              <span class="strike">
                {{compare_at_price_text}}
              </span>&nbsp; 
            {{/strike}}
            {{price_text}}
          </p>
          <p 
            class="collection-card-promo-price mobile" 
            data-tags="{{tags}}" 
            data-currency="{{currency}}" 
            data-price="{{price}}">
          </p>
        </div>
        </div>
        <div
          {{#promoPricingIsRight}}
          class="md:tw-hidden" 
          {{/promoPricingIsRight}}
          style="padding-left: 6px">
        ${promoPricingTPL}
        </div>
      </div>
    </product-card>
    {{/data}}
  `;

  var H = Hogan.compile(tpl);
  

  if (typeof data != 'object') {
    var uri;
    if (style == 'collection') uri = '/carousels/' + id;
    else if (style == 'related') uri = '/recommendations/' + id;

    uri += '?c=' + Shopify.currency.active + '|' + Shopify.currency.rate + '|' + Shopify.country;

    DK.api('GET', uri, function (response) {
      DK.renderCarousel(id, style, response.data);
    });
    return;
  } else {
    var target = false;
    var sliderTarget = false;
    if (typeof id == 'object') {
      id.attr('data-carousel-id', 999);
      target = id;
      targetSlider = '#' + target.attr('id');
    } else {
      target = $('div[data-carousel-id=' + id + ']');
      targetSlider = "div[data-carousel-id='" + id + "']";
    }

    response = {};
    response.data = data;

    for (var i in response.data) {
      if (response.data[i].vendor.substring(0, 2).toLowerCase() == "x_") {
        response.data[i].vendor = "";
      }

      response.data[i].strike = false;

      if (typeof response.data[i].price === "number") {
        response.data[i].price_text = DKi18nPriceFormatter(response.data[i].price * 100, {formatOnly: true});
        response.data[i].price = response.data[i].price.toFixed(2);
      }

      if (typeof response.data[i].compare_at_price === "number") {
        response.data[i].compare_at_price_text = DKi18nPriceFormatter(response.data[i].compare_at_price * 100, {formatOnly: true});
        response.data[i].compare_at_price = response.data[i].compare_at_price.toFixed(2);
      }

      if (parseFloat(response.data[i].price) < parseFloat(response.data[i].compare_at_price))
        response.data[i].strike = true;

      cMatrix[response.data[i].id] = [];

      for (var s in response.data[i].sizes) {
        cMatrix[response.data[i].id].push({
          title: response.data[i].sizes[s].size,
          id: response.data[i].sizes[s].variant_id,
          sku: response.data[i].sizes[s].sku,
          quantity: response.data[i].sizes[s].inventory,
          clearance: response.data[i].clearance,
          parentTags: response.data[i].tags || "",
        });
      }

      if (typeof response.data[i].images == "object") {
        response.data[i].image = response.data[i].images[0].src;
        if (typeof response.data[i].images[1] == "object") {
          response.data[i].image2 = response.data[i].images[1].src;
        }
      }

      const productPriceCents =
        window.Shopify.country.toLowerCase() === "jp"
          ? parseFloat(response.data[i].price)
          : parseFloat(response.data[i].price) * 100;

      const promoPricingData = calculatePromoPrice(
        productPriceCents,
        response.data[i].tags.split(",").map((tag) => tag.trim())
      );

      response.data[i].promo_price_text = promoPricingData.text?.trim();

      response.data[i] = addLoyaltyDataToProductCard(response.data[i]);

      response.data[i].JSON = JSON.stringify(data[i]);

    }

    const discountSettings = getDiscountCodeSettings();

    response.promoPricingIsRight = discountSettings?.productCardPosition === "right";

    var html = H.render(response);

    target.html(html);

    if (style == 'cart') {
      snapCollection(target, 'mslice', 2.5);
    } else {
      var slicer = false;
      if ($(window).width() <= 500) slicer = 2.25;
      if ($(window).width() > 500) slicer = 3.25;
      if ($(window).width() >= 1000) slicer = 4.25;
      if ($(window).width() >= 1300) slicer = 5.25;
      if ($(window).width() >= 1600) slicer = 6.25;

      snapCollection(target, 'slice', slicer);
    }

    
    $('img[data-src2]', target).mouseover(function () {
      var src2 = $(this).attr('data-src2');
      $(this).attr('src', src2);
    });
    $('img[data-src2]', target).mouseout(function () {
      var src = $(this).attr('data-src');
      $(this).attr('src', src);
    });

    setupSlide(targetSlider);
    if (typeof callback == 'function') {
      callback();
    }
  }
};

DK.renderCarousel2 = function (id, etag) {
  if (typeof style != 'string') style = 'collection';

  var tpl =
    '{{#data}}<product-card data-test-id="product-card" data-id="{{id}}" data-handle="{{handle}}" class="product-card render-car-2" data-clearance="{{clearance}}" data-compare-at-price="{{compare_at_price}}" data-price="{{price}}"> <div class="card-actions"><div class="card-actions-sale" data-pid="{{id}}"></div><swym-wishlist-btn class="add-to-wishlist-btn tw-absolute tw-right-0" data-wish-action="addToWishlist" data-product-id="{{id}}" data-product-handle="{{handle}}"></swym-wishlist-btn></div><div class="card-content"> <div class="image-holder loading" data-quickview-container> <a href="/products/{{handle}}"> <img loading="lazy" fetchpriority="low" data-id="{{id}}" data-card-status=0 data-src="{{image}}" alt="" class="grid-image" data-src2="{{image2}}"></a> </div><div class="content-holder"><div style="display:flex;justify-content:space-between;"><p class="product-title"><a href="/products/{{handle}}">{{title}}</a></p><p class="product-price desktop">{{#strike}}<span class="strike">{{compare_at_price_text}}</span>&nbsp; {{/strike}}{{price_text}}</p></div><p class="vendor-name">{{#vendor}}{{vendor}}{{/vendor}}</p><p class="product-price mobile">{{#strike}}<span class="strike">{{compare_at_price_text}}</span>&nbsp; {{/strike}}{{price_text}}</p></div></div></product-card>{{/data}}';
  var H = Hogan.compile(tpl);

  var uri;
  uri = '/carousels/_new?ids=' + id;

  uri += '&c=' + Shopify.currency.active + '|' + Shopify.currency.rate + '|' + Shopify.country;

  DK.api('GET', uri, function (response) {
    for (var i in response.data) {
      if (response.data[i].vendor.substring(0, 2).toLowerCase() == 'x_') {
        response.data[i].vendor = '';
      }
      response.data[i].strike = false;
      if (parseFloat(response.data[i].price) < parseFloat(response.data[i].compare_at_price))
        response.data[i].strike = true;

      cMatrix[response.data[i].id] = [];
      for (var s in response.data[i].variants) {
        cMatrix[response.data[i].id].push({
          title: response.data[i].variants[s].size,
          id: response.data[i].variants[s].variant_id,
          sku: response.data[i].variants[s].sku,
          quantity: response.data[i].variants[s].inventory,
          clearance: response.data[i].clearance,
          parentTags: response.data[i].tags || "",
        });
      }

      var rolloverFound = false;
      for (var image in response.data[i].images) {
        var imageObject = response.data[i].images[image];
        if (imageObject.alt == 'base') {
          response.data[i].image = imageObject.src;
        } else {
          if (!rolloverFound) {
            response.data[i].alternative = imageObject.src;
          }
          if (imageObject.alt == 'rollover') {
            response.data[i].image2 = imageObject.src;
            rolloverFound = true;
          }
        }
      }
      if (typeof response.data[i].image2 != 'string') {
        response.data[i].image2 = response.data[i].alternative;
      }

      const productPriceCents =
      window.Shopify.country.toLowerCase() === "jp"
        ? parseFloat(response.data[i].price)
        : parseFloat(response.data[i].price) * 100;

      const promoPricingData = calculatePromoPrice(
        productPriceCents,
        response.data[i].tags.split(",").map((tag) => tag.trim())
      );

      response.data[i].promo_price_text = promoPricingData.text?.trim();

      response.data[i] = addLoyaltyDataToProductCard(response.data[i]);
    }

    var html = H.render(response);
    var cobject = $('div[data-carousel2=' + etag + ']');

    cobject.html(html);

    if (!DK.isMobile) {
      snapCollection(cobject, 'direct', 350);
    } else {
      snapCollection(cobject, 'slice', 1.7);
    }

    $('img[data-src2]', cobject).mouseover(function () {
      var src2 = $(this).attr('data-src2');
      $(this).attr('src', src2);
    });
    $('img[data-src2]', cobject).mouseout(function () {
      var src = $(this).attr('data-src');
      $(this).attr('src', src);
    });

    setupSlide("div[data-carousel2='" + etag + "']");
    $(window).on('resize', function () {
      setupSlide("div[data-carousel2='" + etag + "']");
    });
  });
};

$(window).scroll(function () {
  if (typeof lazyLoad == 'function') {
    lazyLoad();
  }
});

DK.mountResponse = false;
DK.mount = function () {
  if ($('.mountTo').length > 0) {
    var preview = DK.getQuery('_xpreview');
    var fetch;
    var mta = window.location.pathname;
    if (preview) {
      fetch = '/pages/' + preview;
      $('body').append(
        '<div style="z-index: 99999; position: fixed; top: 20px; right: 20px; display: inline; width: 400px; text-align: center; background-color: yellow; padding: 10px;">In-Preview Mode</div>'
      );
    } else {
      fetch = '/pages/_active?market=' + Shopify.country + '&mount=' + mta;
    }

    DK.api('GET', fetch, function (response) {
      if (preview) {
        var myPackage = {};
        myPackage.data = [];
        response.data.bypass = true;
        myPackage.data.push(response.data);
      } else {
        var myPackage = response;
      }

      if (response.status == 1) {
        DK.mountResponse = myPackage;
        DK.renderMount(myPackage);

        if (window.location.href == 'https://www.dollskill.com/') {
          $.each($('.hrow'), function (k, v) {
            /* if(k == 2 || k == 3) {
                  $(this).css('margin-bottom','0px');
                  $('a', $(this)).css('display','block').css('margin-bottom','-4px');
                  $('.image a', $(this)).css('display','block').css('margin-bottom','-4px');
                  $('.image a img', $(this)).css('max-height','618px');
                }*/

            if ($(window).width() > 1000) {
              if (k == 2 || k == 3) {
                // $(this).css('width','50%').css('float','left').css('padding','0px').css('margin-bottom','3.72vw').css('margin-top','-3.92vw');
              }
              if (k == 2 || k == 3) {
                //$(this).css('margin-top','-4.92vw');
              }
            }
            if ($(window).width() <= 1000) {
              if (k == 1 || k == 2) {
                //$(this).css('margin-bottom','0');
              }
            }
          });
        }
      }
    });
  }
};

$(window).resize(function () {
  if (DK.mountResponse) {
    /* DK.renderMount(DK.mountResponse);*/
  }
});

/* for homepage - custom pages */
DK.renderMount = function (response) {
  var pd = response.data;
  var outHtml = '';
  var isCarouselSilder = false;
  var carouselSliderData = {};
  $.each(response.data, function (k, pd) {
    if (
      ($(window).width() <= 600 && pd.is_mobile == true) ||
      ($(window).width() > 600 && pd.is_mobile == false) ||
      typeof pd.bypass == 'boolean'
    ) {
      $.each(pd.pagedata, function (bk, block) {
        if (block.type == 'image') {
          var marginHtml = 'style="';
          if (typeof block.margintop != 'undefined' && block.margintop) {
            marginHtml += 'margin-top: ' + block.margintop + ' !important;';
          }
          if (typeof block.marginbottom != 'undefined' && block.marginbottom) {
            marginHtml += 'margin-bottom: ' + block.marginbottom + ' !important;';
          }
          marginHtml += '"';

          outHtml +=
            '<div data-tag="' + block.etag + '" class="image-block hrow" ' + marginHtml + '>';
          if (block.link)
            outHtml += '<a data-link-track="' + block.src + '" href="' + block.link + '">';
          outHtml += '<img src="' + block.src + '">';
          if (block.link) outHtml += '</a>';
          outHtml += '</div>';
        }

        if (block.type == 'image3x' || block.type == 'image2x' || block.type == 'image4x') {
          var marginHtml = 'style="';
          if (typeof block.margintop != 'undefined' && block.margintop) {
            marginHtml += 'margin-top: ' + block.margintop + ' !important;';
          }
          if (typeof block.marginbottom != 'undefined' && block.marginbottom) {
            marginHtml += 'margin-bottom: ' + block.marginbottom + ' !important;';
          }
          marginHtml += '"';

          outHtml +=
            '<div data-tag="' +
            block.etag +
            '" class="hrow ' +
            block.type +
            '" ' +
            marginHtml +
            '>';

          if (block.type == 'image4x') {
            for (var i in block.carousel) {
              bcdata = block.carousel[i];
              outHtml += '<div class="image" style="width: 25%; float: left;">';
              if (bcdata['link'])
                outHtml +=
                  '<a data-link-track="' + bcdata['src'] + '" href="' + bcdata['link'] + '">';
              outHtml += '<img src="' + bcdata['src'] + '">';
              if (bcdata['link']) outHtml += '</a>';
              outHtml += '<div style="clear: both"></div></div>';
            }
          } else {
            var countUp = block.type == 'image3x' ? 3 : 2;

            for (i = 1; i <= countUp; i++) {
              outHtml += '<div class="image">';
              if (block['link' + i])
                outHtml +=
                  '<a data-link-track="' +
                  block['image' + i] +
                  '" href="' +
                  block['link' + i] +
                  '">';
              outHtml += '<img src="' + block['image' + i] + '">';
              if (block['link' + i]) outHtml += '</a>';
              outHtml += '</div>';
            }
          }
          outHtml += '</div>';
        }

        if (block.type == 'grid') {
          outHtml +=
            '<div class="hrow slider"><div class="scroller sliding slider.' +
            block.shopify_cid +
            '" data-carousel-id="' +
            block.shopify_cid +
            '"></div></div>';
        }
        if (block.type == 'grid2') {
          var implodes = [];
          for (var bsi in block.strategy) {
            implodes.push(block.strategy[bsi].shopify_id);
          }
          var implodedIds = implodes.join(',');
          outHtml +=
            '<div class="hrow slider"><div class="scroller sliding slider.' +
            block.etag +
            '" data-carousel2="' +
            block.etag +
            '" data-carousel2-ids="' +
            implodedIds +
            '"></div></div>';
        }
        if (block.type == 'carousel') {
          var ctest = false;
          if ($(window).width() > 600) {
            ctest = true;
          }

          if (!ctest) {
            isCarouselSilder = true;
            carouselSliderData.factor = block.factor != '' ? block.factor : 3;
            carouselSliderData.mright = block.mright != '' ? block.mright : 0;
            carouselSliderData.mbottom = block.mbottom != '' ? block.mbottom : 0;
            var carouselData = block['carousel'];
            outHtml +=
              '<div data-tag="' +
              block.etag +
              '" class="center-carousel-slider ' +
              block.type +
              '" style="margin-bottom: ' +
              carouselSliderData.mbottom +
              'px">';
            for (i = 0; i < carouselData.length; i++) {
              var carouselImg = carouselData[i];
              outHtml +=
                '<div class="image"><a data-link-track="' +
                carouselImg['src'] +
                '" href="' +
                carouselImg.link +
                '"><img data-tag="' +
                carouselImg.etag +
                '" src="' +
                carouselImg['src'] +
                '"></a></div>';
            }
            outHtml += '</div>';
            //outHtml += '<style> .center-carousel-slider .slick-slide{margin: 0;display: flex;align-items: center;justify-content: center;transform: scale(0.9);transition: all 0.4s ease-in-out;}.center-carousel-slider .slick-slide, .center-carousel-slider .slick-slide[aria-hidden="true"]:not(.slick-cloned) ~ .slick-cloned[aria-hidden="true"] {transform: scale(0.9, 0.9);transition: all 0.4s ease-in-out;}.center-carousel-slider .slick-center,.center-carousel-slider .slick-slide[aria-hidden="true"]:not([tabindex="-1"]) + .slick-cloned[aria-hidden="true"] {transform: scale(1);}.center-carousel-slider .slick-current.slick-active{transform: scale(1);}</style>';
            outHtml +=
              '<style> .center-carousel-slider .slick-slide{margin-right: ' +
              carouselSliderData.mright +
              'px; align-items: center;justify-content: center;}.slick-slide img{width:100%}</style>';
          } else {
            var carouselData = block['carousel'];
            if (pd.id == 348) {
              var factor = 4.3;
            } else {
              factor = parseFloat(block.factor);
            }
            var ewidth = $('#MainContent').width() / factor;

            outHtml +=
              '<div class="hrow slider"><div class="scroller sliding slider.' +
              block.etag +
              '" data-ih-carousel="' +
              block.etag +
              '">';
            for (i = 0; i < carouselData.length; i++) {
              var carouselImg = carouselData[i];
              outHtml +=
                '<div class="image" style="width: ' +
                ewidth +
                'px; margin-right: 10px; float: none; display: inline-block;"><a data-link-track="' +
                carouselImg['src'] +
                '" href="' +
                carouselImg.link +
                '"><img style="width: 100%" data-tag="' +
                carouselImg.etag +
                '" src="' +
                carouselImg['src'] +
                '"></a></div>';
            }
            outHtml += '</div></div>';
          }
        }
      });
    }
  });

  $('.mountTo').html(outHtml);

  if (isCarouselSilder) {
    $('.center-carousel-slider').slick({
      centerMode: true,
      centerPadding: '60px',
      slidesToShow: carouselSliderData.factor,
      draggable: true,

      autoplay: true,
      autoplaySpeed: 4000,
      arrows: false,
      pauseOnFocus: true,
      pauseOnHover: true,
      swipe: true,
      swipeToSlide: true,
      responsive: [
        {
          breakpoint: 768,
          settings: {
            arrows: false,
            centerMode: true,
            centerPadding: '40px',
            slidesToShow: carouselSliderData.factor,
            autoplay: true,
            autoplaySpeed: 4000,
            pauseOnFocus: false,
            pauseOnHover: false,
          },
        },
        {
          breakpoint: 480,
          settings: {
            arrows: false,
            centerMode: true,
            centerPadding: '40px',
            slidesToShow: 1,
            autoplay: true,
            autoplaySpeed: 4000,
            pauseOnFocus: false,
            pauseOnHover: false,
          },
        },
      ],
    });

    //$(".center-carousel-slider").slick({centerMode:!0,centerPadding:"60px",slidesToShow:3,autoplay:!0,autoplaySpeed:4e3,arrows:!1,pauseOnFocus:!1,pauseOnHover:!1,responsive:[{breakpoint:768,settings:{arrows:!1,centerMode:!0,centerPadding:"40px",slidesToShow:3,autoplay:!0,autoplaySpeed:400,pauseOnFocus:!1,pauseOnHover:!1}},{breakpoint:480,settings:{arrows:!1,centerMode:!0,centerPadding:"40px",slidesToShow:1,autoplay:!0,autoplaySpeed:400,pauseOnFocus:!1,pauseOnHover:!1}},]})
    //$(".center-carousel-slider").slick({centerMode:!0,centerPadding:"60px",slidesToShow:3,autoplay:!0,autoplaySpeed:4e3,arrows:!1,responsive:[{breakpoint:768,settings:{arrows:!1,centerMode:!0,centerPadding:"40px",slidesToShow:3,autoplay:!0,autoplaySpeed:4e3}},{breakpoint:480,settings:{arrows:!1,centerMode:!0,centerPadding:"40px",slidesToShow:1,autoplay:!0,autoplaySpeed:4e3}}]})
  }

  $('div[data-ih-carousel').each(function () {
    var ihid = $(this).attr('data-ih-carousel');
    setupSlide("div[data-ih-carousel='" + ihid + "']");
    DK.scheck(ihid);
    window.setTimeout(function () {
      DK.cycle(ihid);
    }, 4000);
  });

  $('div[data-carousel-id]').each(function () {
    DK.renderCarousel($(this).attr('data-carousel-id'));
  });
  $('div[data-carousel2-ids]').each(function () {
    DK.renderCarousel2($(this).attr('data-carousel2-ids'), $(this).attr('data-carousel2'));
  });
  /* click tracking on hp/lp */
  $('a[data-link-track]').click(function () {
    var mt = $(this).attr('data-link-track');
    var href = window.location.pathname;

    DK.set('lpClick', { i: mt, p: href });
    return true;
  });
};

DK.scheck = function (id) {
  var obj = $('div[data-ih-carousel=' + id + ']');
  var cycleKey = 'timeout' + id;
  obj.mouseenter(function () {
    if (typeof DK[cycleKey] == 'number') {
      window.clearTimeout(DK[cycleKey]);
    }
  });
};

DK.cycle = function (id) {
  var cycleKey = 'timeout' + id;

  var obj = $('div[data-ih-carousel=' + id + ']');
  var step = obj.attr('step');

  if (!step) {
    step = 0;
    obj.attr('step', step);
  }

  next = parseInt(step) + 1;

  var w = $('.image', obj).width() + 10;
  var sl = w * next;

  var maxScroll = obj[0].scrollWidth - obj[0].clientWidth + $('.image', obj).length * 30;

  if (sl > maxScroll) {
    sl = 0;
    speed = 900;
    next = 0;
  } else {
    speed = 500;
  }

  obj.animate({ scrollLeft: sl }, speed);
  obj.attr('step', next);

  DK[cycleKey] = window.setTimeout(function () {
    DK.cycle(id);
  }, 4000);
};

const loyaltySetup = async (cart) => {
  let cartCopy = { ...cart };

  const loyalty_settings_element = document.querySelector("[data-loyalty-settings]");
  const loyalty_settings = loyalty_settings_element && JSON.parse(loyalty_settings_element?.textContent);
  
  let combined_discount = 0;
  let combined_cashback = 0;
  let active_in_current_country = false;

  if (
    loyalty_settings && 
    (Shopify.country == "US" && loyalty_settings.loyalty_region == "domestic" || 
    Shopify.country !== "US" && loyalty_settings.loyalty_region == "international" || 
    loyalty_settings.loyalty_region == "global")) {
      active_in_current_country = true;
  }
  
  let { 
    loyalty_pricing_cart,
    eligible_product_tags,
    stash_text_background_color,
    loyalty_pricing_text_color,
    loyalty_pricing_bg_color,
    loyalty_dollars_off,
    loyalty_percent_off,
    loyalty_cash_back,
    loyalty_product,
    loyalty_mini_cart_promo_title,
    loyalty_mini_cart_promo_value_prop,
    loyalty_mini_cart_disclaimer_text,
    loyalty_mini_cart_disclaimer_link,
    loyalty_mini_cart_disclaimer_link_text,
    loyalty_product_selling_plan_id,
    loyalty_product_tags,
    show_loyalty_discount,
    show_loyalty_subtotal,
    show_loyalty_total,
    loyalty_discount_type
  } = loyalty_settings;
  if (loyalty_pricing_cart && active_in_current_country) {
    if (cartCopy?.items?.every((item) => item.tags)) {
      if (DK.customer && DK.stashCash !== 0) cartCopy.stash_cash = DKi18nPriceFormatter(DK.stashCash * 100);
      cartCopy.loyalty_product_in_cart = false;
      cartCopy.eligible_loyalty_products = cartCopy.items.some((item) => eligible_product_tags.split(',').some((tag) => item.tags.includes(tag)));
      if (loyalty_product) {
        cartCopy.stash_text_background_color = stash_text_background_color;
        cartCopy.loyalty_product = loyalty_product;
        cartCopy.loyalty_product_tags = loyalty_product_tags;
        cartCopy.loyalty_product_selling_plan_id = loyalty_product_selling_plan_id;
        cartCopy.loyalty_mini_cart_promo_title = loyalty_mini_cart_promo_title.replace("Stash", `<span class=' tw-text-white' style='font-family: med25; padding: 4px 8px;letter-spacing: -1.6px; background-color: ${stash_text_background_color}; '>Stash</span>`);
        cartCopy.loyalty_mini_cart_promo_value_prop = loyalty_mini_cart_promo_value_prop
          .replace(/<[^>]*>/g, ',')
          .split(',')
          .map((item) => item.trim())
          .filter(item => item.length > 0);
        cartCopy.loyalty_mini_cart_disclaimer_text = loyalty_mini_cart_disclaimer_text;
        cartCopy.loyalty_mini_cart_disclaimer_link = loyalty_mini_cart_disclaimer_link;
        cartCopy.loyalty_mini_cart_disclaimer_link_text = loyalty_mini_cart_disclaimer_link_text;
        cartCopy.loyalty_cash_back = loyalty_cash_back;
        cartCopy.loyalty_discount_type = loyalty_discount_type;
        cartCopy.loyalty_discount_amount = loyalty_discount_type == "fixed_amount" ? loyalty_dollars_off : loyalty_percent_off;
        cartCopy.loyalty_show_fixed_discount = loyalty_discount_type == "fixed_amount";
        cartCopy.loyalty_percent_off = loyalty_percent_off;
        cartCopy.loyalty_product_in_cart = cartCopy.items.some((item) => item.id == loyalty_product);
        cartCopy.show_loyalty_product_in_cart = !cartCopy.loyalty_product_in_cart;
        cartCopy.show_loyalty_promo_banner = cartCopy.loyalty_product_in_cart && cartCopy.items.length === 2;
        cartCopy.show_loyalty_discount = show_loyalty_discount;
        cartCopy.show_loyalty_subtotal = show_loyalty_subtotal;
        cartCopy.show_loyalty_total = show_loyalty_total;
      }
      cartCopy.items.map((item) => {
        const loyalty_discount_price = !item.tags.includes("inveterate-product") && 
        !item.tags.includes("inveterate-signup-gift") ? item.final_line_price * ((100 - loyalty_percent_off) / 100) : item.original_line_price;
        
        const loyalty_discount_amount = (item.discounted_price * item.quantity) - loyalty_discount_price;
        const loyalty_cashback_value = cartCopy.loyalty_product_in_cart || DK.hasLoyalty ? (item.discounted_price * item.quantity) *(loyalty_cash_back / 100) : loyalty_discount_price * (loyalty_cash_back / 100);
        
        item.is_not_gift_card = !item.product_type?.includes("Gift Card"); 
        item.show_discount_strike_through = item.original_line_price > item.final_line_price;
        item.discount_strike_through_price_display = DKi18nPriceFormatter(item.original_line_price, {formatOnly: true});
        if (loyalty_product) {
          item.loyalty_discount_available = 
            item.tags.some((tag) => eligible_product_tags.includes(tag)) && 
            !item.tags.includes("inveterate-product") && 
            !item.tags.includes("inveterate-signup-gift");
          item.loyalty_free_gift = (item.tags.includes("inveterate-signup-gift") || item.tags.includes("gwp-gift-product")) && item.price == 0;
          item.variant_title = (item.tags.includes("inveterate-signup-gift") || item.tags.includes("gwp-gift-product")) && item.price == 0 ? "FREE" : item.variant_title;
          item.loyalty_discount = DKi18nPriceFormatter(loyalty_discount_price, {formatOnly: true});
          item.loyalty_cashback = DKi18nPriceFormatter(loyalty_cashback_value, {formatOnly: true});
          item.loyalty_pricing_text_color = loyalty_pricing_text_color;
          item.loyalty_pricing_bg_color = loyalty_pricing_bg_color;
          // item.display_loyalty_pricing = item.loyalty_discount_available && !DK.hasLoyalty && item.selling_plan_id === 0 && !item.product_type?.includes("Gift Card"); 
          item.display_loyalty_pricing = false; 
         
          if (
            item.loyalty_discount_available && 
            item.selling_plan_id === 0 && 
            !item.product_type?.includes("Gift Card") && 
            !item.product_type?.includes("Insurance")
          ) {
            combined_cashback += loyalty_cashback_value
            combined_discount += loyalty_discount_amount
            
          }

          if (item.product_type?.includes("Insurance")) {
            cartCopy.original_total_price -= item.original_line_price;
            cartCopy.total_price -= item.original_line_price;
          }
        }
      })
      if (cartCopy.loyalty_discount_type == "fixed_amount") {
        combined_discount = loyalty_dollars_off;
      }

      cartCopy.discounted_subtotal = DKi18nPriceFormatter((cartCopy.total_price - combined_discount), {formatOnly: true});
      cartCopy.show_discounted_subtotal = cartCopy.loyalty_discount_type == "percentage";
      cartCopy.loyalty_discount_amount = formatPrice(combined_discount);
      cartCopy.loyalty_discount_amount_text = DKi18nPriceFormatter(combined_discount, {formatOnly: true});
      cartCopy.loyalty_cashback_amount = DKi18nPriceFormatter(combined_cashback, {formatOnly: true});
      
      cartCopy.loyalty_dollars_off = DKi18nPriceFormatter(loyalty_dollars_off, {formatOnly: true});
      cartCopy.loyalty_dollars_off_text = cartCopy.loyalty_dollars_off.split(".")[0]; 
      cartCopy.show_loyalty_product_in_cart = !DK.hasLoyalty && cartCopy.show_loyalty_product_in_cart;
      cartCopy.loyalty_benefits_available = loyalty_pricing_cart && combined_cashback != 0 && combined_discount != 0;
      cartCopy.show_cash_back = combined_cashback != 0 && (DK.hasLoyalty || cartCopy.loyalty_product_in_cart);

    } else {
      cartCopy = await new Promise(resolve => {
        DK.getCart((cart) => {
          const newCart = loyaltySetup(cart);
          resolve(newCart);
        });
      });
    }
  } else {
    cartCopy.items.map((item) => item.is_not_gift_card = !item.product_type?.includes("Gift Card"));
  }
  return cartCopy;
}

DK.getCart = function (callback) {
  jQuery.ajax({
    url: '/cart',
    headers: { "Accept": "*/*"},
    data: { "view": "json" },
    type: 'get',
    dataType: 'json',
    success: function (x) {
      loyaltySetup(x).then((response) => {
        response.item_count = 0;
        for (var rr in response.items) {
          if (response.items[rr].product_type != "Insurance") {
            response.item_count += response.items[rr].quantity;
          }
        }
        if (response.item_count > 0) {
          $('#bag-count').html(response.item_count).closest('[data-mini-cart-btn]').addClass('not-empty');
          if (response.item_count > 9) {
            $('#bag-count').addClass('dd');
          } else {
            $('#bag-count').removeClass('dd');
          }
        } else {
          $('#bag-count').html(response.item_count).closest('[data-mini-cart-btn]').removeClass('not-empty');
        }
        callback(response);
      });
    },
    // error: function(XMLHttpRequest, textStatus, errorThrown) {
    //   console.log("Error: " + errorThrown);
    //   window.location.href = "/cart?fallback=true"
    // },
    // timeout: 3000
  });
};

DK.addToCart = function (vid, callback, tags = "", lineItemProps = {}, sellingPlanId = 0, parentID = undefined) {
  fetch("/cart.js")
    .then((response) => {
      if (response.ok) return response.json();

      console.error("Failed to fetch cart data", response);
      return null;
    })
    .then((cart) => {
      const legacyProcess = () => {
        var properties = lineItemProps;
        var csource = "pdp";
        if (!properties["_inveterate_subscription"]) {
          if (typeof DK.qcontext == "object") {
            csource = "qv";

            if (typeof DK.qcontext.multiplier == "number") {
              properties["_multiplier"] = DK.qcontext.multiplier;
            }
            if (typeof DK.qcontext.op == "string") {
              properties["_op"] = parseFloat(DK.qcontext.op);
            }
            if (typeof DK.qcontext.preorder == "string") {
              properties["_preorder"] = DK.qcontext.preorder;
            }
            if (typeof DK.qcontext.microcategory == "string") {
              properties["_microcategory"] = DK.qcontext.microcategory;
            }
            if (typeof DK.qcontext.category == "string") {
              properties["_category"] = DK.qcontext.category;
            }
            if (typeof DK.qcontext.subcategory == "string") {
              properties["_subcategory"] = DK.qcontext.subcategory;
            }
            if (typeof DK.qcontext.clearance == "string") {
              if (DK.qcontext.clearance == "false") properties["_clearance"] = false;
              if (DK.qcontext.clearance == "true") properties["_clearance"] = true;
            }
            if (typeof DK.qcontext.pid == "string") {
              properties["_pid"] = parseInt(DK.qcontext.pid);
            } else {
              properties["_pid"] = 1;
            }
          } else if (typeof DK.productData == "object") {
            if (
              typeof DK.productData.giftcard_id == "string" ||
              typeof DK.productData.giftcard_id == "number"
            ) {
              properties["_giftcard_id"] = DK.productData.giftcard_id;
            }
            if (typeof DK.productData.preorder == "string") {
              properties["_preorder"] = DK.productData.preorder;
            }
            if (typeof DK.productData.clearance == "boolean") {
              properties["_clearance"] = DK.productData.clearance;
            }
            if (typeof DK.productData.price == "string") {
              properties["_op"] = parseFloat(DK.productData.price) / 100;
            }
            if (typeof DK.productData.multiplier == "number") {
              properties["_multiplier"] = DK.productData.multiplier;
            }
            if (typeof DK.productData.microcategory == "string") {
              properties["_microcategory"] = DK.productData.microcategory;
            }
            if (typeof DK.productData.category == "string") {
              properties["_category"] = DK.productData.category;
            }
            if (typeof DK.productData.subcategory == "string") {
              properties["_subcategory"] = DK.productData.subcategory;
            }
            if (typeof DK.productData.id == "number") {
              properties["_pid"] = DK.productData.id;
            } else {
              properties["_pid"] = 1;
            }
          }
        }

        const loyaltySettingsEl = document.querySelector("[data-loyalty-settings]");
        const loyaltySettings = loyaltySettingsEl && JSON.parse(loyaltySettingsEl?.textContent);

        const promoCodeEl = document.querySelector("#promo-code-settings");
        const promoCode = promoCodeEl && JSON.parse(promoCodeEl.innerHTML);

        const gwpCodeEl = document.querySelector("[data-gwp-settings]");
        const gwpSettings = gwpCodeEl && JSON.parse(gwpCodeEl.innerHTML);

        const eligibleTagsSplit = promoCode?.eligibleTags.split(",");

        let discount_eligible = false;

        eligibleTagsSplit?.forEach((eligibleTag) => {
          if (tags.includes(eligibleTag)) {
            discount_eligible = true;
          }
        });

        properties["_discount_eligible"] = discount_eligible;

        if (typeof lineItemProps["_gwp-gift-product-threshold"] === "undefined" && gwpSettings?.gwp_trigger_threshold && gwpSettings?.gwp_product_variant_id == vid) {
          properties["_gwp-gift-product-threshold"] = `${gwpSettings?.gwp_trigger_threshold}`;
        }

        // add rebuy attribution if present
        Object.keys(lineItemProps).forEach((key) => {
          if (key === "_source" || key === "_attribution" || key === "_widget_id") {
            properties[key] = lineItemProps[key];
          }
        });

        // check for rebuy discovery cookies
        const rebuyDiscoveryCookies = document.cookie.split("; ").filter(row => row.startsWith("rebuy_product_id="));
        if (rebuyDiscoveryCookies.length > 0) {
          const rebuyDiscoveryProductIds = rebuyDiscoveryCookies.map(cookie => cookie.split("=")[1]);

          // check if we're adding a rebuy product to the cart
          if (typeof parentID !== "undefined" && typeof properties["_source"] === "undefined" && rebuyDiscoveryProductIds.includes(parentID.toString())) {
            properties["_source"] = "Rebuy";
            properties["_attribution"] = "Product Discovery";
            properties["_widget_id"] = "201877";
          }
        }

        const finalSaleTags = new Set(['final_sale', 'final_sale_item', 'finalsale']);
        const productTags = Array.isArray(tags) ? tags : tags.split(",");
        const isFinalSale = productTags.some(tag => 
          finalSaleTags.has(tag) || tag.includes('final_sale:boolean:true')
        );

        if (isFinalSale) {
          properties["_final_sale"] = true;
        }

        let formData = {
          items: [
            {
              id: vid,
              quantity: 1,
              properties: properties,
            },
          ],
        };

        if (sellingPlanId) {
          formData.items[0].selling_plan = sellingPlanId;
        }

        const urlParams = new URLSearchParams(window.location.search);
        const isStorePurchase = urlParams.get("store_purchase") || false;

        if (
          tags.includes("inveterate-product") &&
          loyaltySettings?.loyalty_gwp_product &&
          loyaltySettings?.loyalty_gwp_product_available == true &&
          isStorePurchase !== "true"
        ) {
          formData.items.unshift({
            id: loyaltySettings.loyalty_gwp_product,
            quantity: 1,
            properties: {},
          });
        }

        if (
          gwpSettings?.gwp_enabled &&
          gwpSettings?.gwp_trigger_type == "tag" && tags.includes(gwpSettings?.gwp_trigger_tag) &&
          gwpSettings?.gwp_product_variant_id &&
          gwpSettings?.gwp_product_available == true
        ) {
          formData.items.unshift({
            id: gwpSettings?.gwp_product_variant_id,
            quantity: 1,
            properties: {},
          });
        }

        if (window?.Tapcart && window.Tapcart.isInitialized === true) {
          // format line items for Tapcart
          const items = formData.items.map((item) => {
            const lineItem = {
              variantId: item.id,
              quantity: item.quantity,
              attributes: Object.keys(item.properties).map((key) => ({
                key,
                value: item.properties[key],
              })),
            };

            if (item.selling_plan) {
              lineItem.sellingPlanId = item.selling_plan;
            }

            return lineItem;
          });

          // execute Tapcart add to cart action
          async function addToTapcart() {
            try {
              await window.Tapcart.actions.addToCart({ lineItems: items });
            } catch (error) {
              // Handle the error
              console.error(error);
            } finally {
              window.Tapcart.actions.openScreen({
                destination: { type: "internal", url: "/cart" },
              });
            }
          }

          addToTapcart();

          // return to prevent Shopify add to cart action
          return;
        }

        // if the item is already in the cart, update the quantity and bail 
        const lineItem = cart?.items?.find(item => item?.variant_id === vid);
        if (lineItem && lineItem?.key) {
          DK.updateCartByLineKey(lineItem.key, lineItem.quantity +1);
          return;
        }

        fetch(window.Shopify.routes.root + "cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        })
          .then((response) => response.json())
          .then((data) => {
            if (data?.errors){
              callback(data);
              return;
            }

            DK.getCart(function (data) {
              dataLayer.push({ addedItem: data.items[0] });
              dataLayer.push({ cartItems: data });
              dataLayer.push({ csource: csource });
              dataLayer.push({ event: "addedToCart" });

              var Kcart = {
                total_price: data.total_price / 100,
                $value: data.total_price / 100,
                total_discount: data.total_discount,
                original_total_price: data.original_total_price / 100,
                items: data.items,
              };
              if (typeof item !== "undefined") {
                Kcart = Object.assign(Kcart, item);
              }

              if (typeof _learnq == "object") {
                var _x = _learnq.push(["track", "Added to Cart", Kcart]);
              }

              var ids = [];

              $.each(data.items, function (k, v) {
                ids.push(v.product_id);
              });

              var dkmarket = DK.get("market");
              var dkids = DK.get("ids");
              if (!dkids) dkids = [];
              var callAPI = false;

              if (Shopify.country != dkmarket) callAPI = true;
              if (dkids.join(",") != ids.join(",")) callAPI = true;

              callback(data);
            });
          })
          .catch((error) => {
            console.error("Error:", error);
            $.each(localStorage, function (k, v) {
              if (k.indexOf("_fe") >= 0) localStorage.removeItem(k);
            });
          });
      };

      const { restricted_products } = JSON.parse(
        document.querySelector("[data-restricted-products]").textContent
      );
      
      if (restricted_products.includes(parentID)) {
        let variantInCartCount = 0;

        const sameParentItems = cart.items.filter(
          (item) => item.product_id == parentID
        );

        sameParentItems.forEach((item) => {
          variantInCartCount += item.quantity;
        });

        if (variantInCartCount < 3) {
          legacyProcess();
          return;
        }

        window.DK.renderCart(undefined, undefined, true);
        return;
      } else {
        legacyProcess();
      }
    });
};

DK.freeship = 75;
DK.freeshipOvernight = null;

DK.isLocalStorageFull = () => {
  var totalCharacters = 0;

  // get character length
  for (var key in localStorage) {
    if (!localStorage.hasOwnProperty(key)) continue;

    // keyLength is multiplied by 2 since data is stored in utf-16 format
    var keyLength = (localStorage[key].length + key.length) * 2;
    totalCharacters += keyLength;
  }

  // return false if we're over to 4.5 MB
  return totalCharacters / 1024 > 4500;
};

DK.renderCart = async function (response, attempts = 1, restrictedProduct = false) {

  if (typeof response !== "object") {
    DK.getCart(function (response) {
      DK.renderCart(response, undefined, restrictedProduct);
    });
    return;
  }

  const promoCodeSettingsEl = document.querySelector("#promo-code-settings");
  const discountSettings = promoCodeSettingsEl ? JSON.parse(promoCodeSettingsEl?.innerHTML || "null") : {};

  if (DK.shipScoutActive && !DK.shipScout) DK.checkShipScout();

  var dkmarket = DK.get("xmarket");
  var dkids = DK.get("xids");
  if (!dkids) dkids = [];
  var callAPI = false;

  /** @type {number[]} */
  var ids = [];

  let flp = 0;

  /**
   * @typedef {Object} ResponseItem
   * @property {number} product_id
   * @property {number} sortX
   * @property {number} line_price
   * @property {number | string} final_line_price
   * @property {string} handle
   * @property {number} quantity
   * @property {string} tags
   * @property {{ compare_at_price: number, strike: boolean }} properties
   */

  /** @type {ResponseItem[]} */
  const responseItems = response.items;

  response.has_promo_items = false;
  response.items_subtotal_promo_price = 0;

  for (const [index, item] of responseItems.entries()) {
    const { product_id, line_price, tags} = item;
     
    ids.push(product_id);

    if (
      product_id == 8732999713025 ||
      product_id == 7908679155969 ||
      product_id == 7908679287041 ||
      product_id == 7920129016065 ||
      product_id == 7908685906177 ||
      product_id == 7694550171905 ||
      product_id == 7924657258753 ||
      product_id == 7947685724417 ||
      product_id == 7950321058049
    ) {
      item.sortX = 999;
    } else {
      item.sortX = index;
    }
    item.final_line_price = typeof item.final_line_price === "number" ? (parseFloat(item.final_line_price)) : item.final_line_price;

    const productPrice = item.original_price * item.quantity;

    item.properties.price = DKi18nPriceFormatter(productPrice, { formatOnly: true });

    let compare_at_price = 0;

    try {
      const productDataResponse = await fetch(`/products/${item.handle}.js`);
      const data = await productDataResponse.json();
      compare_at_price = data.compare_at_price * item.quantity;
    } catch(error) {
      if (attempts === 5) {
        console.error(`Failed to fetch product data for ${item.handle} after ${attempts} attempts. Redirecting to fallback cart page`);
        window.location.href = "/cart?fallback=true";
      }

      console.error(error);
      console.error(`Attempt ${attempts} failed to fetch product data for ${item.handle}. Retrying...`);

      setTimeout(() => DK.renderCart(response, attempts + 1), 2000);
      return;
    }

    item.properties.strike = productPrice < compare_at_price;
    item.properties.compare_at_price = DKi18nPriceFormatter(compare_at_price, { formatOnly: true });

    const {price: promoPrice, text: promoPriceText} = calculatePromoPrice(line_price, tags);

    if (product_id != 8732999713025) {
      const final_line_price = parseFloat(item.final_line_price)
      flp += final_line_price;

      response.has_promo_items = promoPrice ? true : response.has_promo_items;

      response.items_subtotal_promo_price += promoPrice || final_line_price;
    }
    const has_active_promo = Object.keys(item.discount_allocations).filter((key) => item?.discount_allocations[key]?.discount_application?.type?.toLowerCase() == "discount_code").length > 0;
    item.has_active_promo = has_active_promo;
    item.promo_price_text = has_active_promo || !discountSettings?.showPromoCodeInCart ? "" : promoPriceText;
    item.formatted_final_line_price = DKi18nPriceFormatter(item.final_line_price, { formatOnly: true });
  };

  if (response.items_subtotal_promo_price > 0) {
    response.items_subtotal_promo_price = DKi18nPriceFormatter(response.items_subtotal_promo_price, { formatOnly: true })
    
    if (!discountSettings) {
      console.error("Discount settings source code not found");
      response.active_promo_code = ""
    }

    const { code } = discountSettings;

    response.active_promo_code = code;
  }
  
  response.items_subtotal_price = (flp / 100).toFixed(2);
  response.items_subtotal_price_text = DKi18nPriceFormatter(flp, { formatOnly: true });
  const loyalty_subtotal = parseFloat(response.items_subtotal_price);
  response.items_subtotal_price_before_loyalty = !isNaN(loyalty_subtotal) ? DKi18nPriceFormatter(loyalty_subtotal * 100, {formatOnly: true}) : response.items_subtotal_price_text;
  response.has_promo_items = discountSettings?.showPromoCodeInCart ? (
    response.items.find((item) => item.has_active_promo) ? false : response.has_promo_items 
  ) : false;
  response.currency_symbol = "$";
  response.show_free_ship = false;
  if (
    response.currency == "USD" ||
    response.currency == "GBP" ||
    response.currency == "CAD" ||
    response.currency == "NZD"
  )
    response.show_free_ship = true;
  if (response.currency == "CAD") {
    response.currency_symbol = "CAD$";
  }
  if (response.currency == "AUD") {
    response.currency_symbol = "A$";
  }
  if (response.currency == "GBP") {
    response.currency_symbol = "£";
  }
  if (response.currency == "EUR") {
    response.currency_symbol = "€";
  }
  if (response.currency == "NZD") {
    response.currency_symbol = "NZ$";
  }

  if (response.currency != "USD") {
    response.currency_symbol = DK.currencyName + " " + DK.currencySymbol;
  }
  response.show_categories = false;

  if (parseInt(response.item_count) == 0) response.show_categories = true;
  if (flp == 0) response.show_categories = true;

  var categories = [];
  categories = [
    {
      name: "Clothing",
      link: "https://dolls-test.myshopify.com/collections/clothing",
    },
    {
      name: "Shoes",
      link: "https://dolls-test.myshopify.com/collections/shoes",
    },
    {
      name: "Plus",
      link: "https://dolls-test.myshopify.com/collections/plus-size-curvy-clothing",
    },
    { name: "Home", link: "https://dolls-test.myshopify.com/collections/home" },
    { name: "Beauty", link: "https://www.dollskill.com/collections/beauty" },
    {
      name: "Accessories",
      link: "https://dolls-test.myshopify.com/collections/accessories",
    },
    {
      name: "Sale",
      link: "https://dolls-test.myshopify.com/collections/clearance",
    },
  ];
  response.categories = categories;

  var country = "US";
  if (Shopify.country == "GB") {
    country = "UK";
    DK.freeship = 75;
  }
  if (Shopify.country == "CA") {
    country = "CA";
    DK.freeship = 100;
  }
  if (Shopify.country == "NZ") {
    country = "NZ";
    DK.freeship = 200;
  }

  if (Shopify.country != "US") {
    DK.freeshipOvernight = null;
  }

  $.each(response.items, function () {
    if (this.id == 43037818716417) this.properties.strike = false;
  });

  // var activateFreeShip = DK.get('freeshippromo1110');
  var activateFreeShip = true;

  if (Shopify.country != "US") activateFreeShip = false;

  response.restrictedProduct = restrictedProduct;

  //var tpl = '<style>.vshorter { margin-bottom: 45px !important; } .drawer1{padding: 0;}.cart-card-actions-fav{font-size: 13px;}.cart-item{position: relative; padding: 16px 0 16px 0; /*margin-bottom: 16px;*/}.cart-image{display: inline-block; text-align: center; width: 100px;}.cart-image img{width: 95%;}.cart-content{position: absolute; left: 120px; display: inline-block; width: 72%;}.cart-top-header p.drawer1-header{margin: 0; padding: 0; padding-top: 20px;}.cart-top-header{display: block;}.cart-items-wrapper .cart-item{border-bottom: 1px solid #ededed; border-top: 1px solid #ededed;}.cart-item:first-child{border-top: 0;}.cart-item:last-child{border-bottom: 2px solid #ededed;}cart-content .vendor-name{color: #a5a5a5; padding: 0; margin: 0; font-size: 11px; font-style: italic;}.cart-content .product-title, .product-price{margin: 0; padding: 0; margin-top: 2px; font-size: 14px;}.cart-content .product-title{white-space: unset;}.product-variant .product-price,{margin-right: 11px; font-size: 14px;}.strike{text-decoration: line-through;}.cart-content .product-variant{margin: 0; padding: 0; margin-top: 4px; font-size: 12px; margin-bottom: 72px;}.cart-content .product-quantity{margin: 0; padding: 0; margin-top: 5px; font-size: 12px;}.cart-icon-holder{position: absolute; right: 5px; top: 10px; text-align: right;}.cart-actions-heart:hover{cursor: pointer;}.button_remove_item{height: 19px; width: 50px; font-size: 13px; padding: 0; margin: 0; margin-top: 68px; display: inline;}.fa-trash-can{font-size: 15px; margin-top: 6px; font-weight: 400;}.hover{cursor: pointer;}.button_remove_item span{cursor: pointer;}.cart-checkout-button{height: 48px; display: block; background-color: black; color: white; margin: 15px 16px; text-align: center;}.cart-checkout-button:hover{opacity: 0.7;}.cart-checkout-button a{font-size: 20px; font-weight: bold; text-transform: uppercase; text-decoration: none; padding-top: 10px; display: inline-block; color: white; width: 100%; height: 40px;}.cart-checkout-button a:visited, .cart-checkout-button a:hover{color: white;}.cart-checkout-extratext{text-align: center; width: 100%; height: 40px;}.cart-subtotal-wrapper{display: flex; margin: 0 0 20px; padding: 20px 0 0; font-size: 16px; font-weight: 900;}.cart-subtotal-wrapper .cart-subtotal{ margin-left: 10px;}.cart-subtotal-wrapper .cart-subtotal-amount{margin-right: 16px;}.animated-progress{width: 277px; height: 5px; border-radius: 2px; margin: 4px auto; overflow: hidden; position: relative; background-color: #cacaca;}.animated-progress span{height: 100%; display: block; width: 0; color: #ff00a8; line-height: 30px; position: absolute; text-align: end;}.upper-threshold .progress{background-color:#FCF071}.progress{background-color: #ff00a8;}@media screen and (max-width: 768px){.cart-subtotal-wrapper .cart-subtotal-amount{margin-right: 9px;}}@media screen and (max-width:600px){.cart-items-wrapper{margin-top:70px;}}</style><div class="cart-top-header centered"> <p class="drawer1-header"><img src="//cdn.shopify.com/s/files/1/0634/6335/8721/t/1/assets/dk_logo_small.svg?v=154960321834099068811654276252"/></p>{{#show_free_ship}}<p style="padding-top: 0px; padding-bottom: 0px; margin-top: 0px; margin-bottom: 0px;"><span id="to-free-ship"></span></p>';
  var tpl =
    '<style>.cart-items-contiainer {margin-bottom:20px;} .cart-item .product-price.red { font-size: 14px; } .cart-items-wrapper .qatc-options{font-size:12px;white-space: pre-wrap;} .checkout-wrapper{width: 100%; position: sticky; z-index: 999; background-color: white; bottom: 0px; padding-top: 1px;} .cart-items-wrapper .scroller .product-card {margin-right: 8px;} .cart-items-wrapper .card-actions-fav {display: none;} .vshorter { margin-bottom: 45px !important; } .drawer1{display: flex; flex-direction: column;padding: 0; width: 480px;} @media (max-width: 619px){.drawer1{ width: 100vw;}}.cart-card-actions-fav{font-size: 13px;}.cart-item{position: relative; padding: 18px 0; margin: 0 16px; display: flex;}.cart-image{display: inline-block; text-align: center; width: 100px;}.cart-image img{width: 95%;}.cart-content{position: absolute; left: 120px; display: inline-block; width: 72%;}.cart-top-header p.drawer1-header{margin: 0; padding: 0; padding-top: 50px;}.cart-top-header{display: block;}[data-product-type="Gift Cards"] .product-variant{display:none;}.cart-items-wrapper .cart-item--dotted{background-image: linear-gradient(to right, #000 15%, rgba(255,255,255,0) 15%); background-position: bottom; background-size: 9px 2px; background-repeat: repeat-x;}.cart-item:first-child{border-top: 0;}.cart-item:last-child{border-bottom: 2px solid #ededed;}cart-content .vendor-name{color: #a5a5a5; padding: 0; margin: 0; font-size: 11px; font-style: italic;}.cart-content .product-title, .cart-item .cart-content span.product-price-main .product-price{margin: 0; padding: 0; margin-top: 2px; font-size: 10px; font-family: med20;}.cart-content .product-title{white-space: unset; font-family: med20;}.product-variant .product-price,{margin-right: 11px; font-size: 14px;}.strike{text-decoration: line-through;}.cart-content .product-variant{margin: 0; padding: 0; margin-top: 4px; font-size: 12px; margin-bottom: 72px;}.cart-content .product-quantity{margin: 0; padding: 0; margin-top: 5px; font-size: 12px;}.cart-icon-holder{position: absolute; right: 5px; top: 10px; text-align: right;}.cart-actions-heart:hover{cursor: pointer;}.button_remove_item{height: 19px; width: 50px; font-size: 13px; padding: 0; margin: 0; margin-top: 68px; display: inline;}.fa-trash-can{font-size: 15px; margin-top: 6px; font-weight: 400;}.hover{cursor: pointer;}.button_remove_item span{cursor: pointer;}.cart-checkout-button{height: 56px; display: block; background-color: black; color: white; margin: 0px 16px 16px; text-align: center;}.cart-checkout-button:hover{opacity: 0.7;}.cart-checkout-button a{font-size: 20px; font-weight: bold; text-transform: uppercase; text-decoration: none; padding-top: 10px; display: inline-block; color: white; width: 100%; height: 40px;}.cart-checkout-button a:visited, .cart-checkout-button a:hover{color: white;}.cart-checkout-extratext{text-align: center; width: 100%; height: 40px;}.cart-subtotal-wrapper{display: flex; justify-content:space-between; flex-direction: column; padding: 20px 16px 0; font-size: 16px; font-weight: 900;}.cart-subtotal-wrapper .cart-subtotal{float: left; font-size: 10px; }.cart-subtotal-wrapper .cart-subtotal-amount{float: right; font-size: 10px; font-family: light20}.animated-progress{width: 277px; height: 5px; border-radius: 2px; margin: 4px auto; overflow: hidden; position: relative; background-color: #cacaca;}.animated-progress span{height: 100%; display: block; width: 0; color: #ff00a8; line-height: 30px; position: absolute; text-align: end;}.upper-threshold .progress{background-color:#FCF071}.progress{background-color: #ff00a8;}#to-free-ship{font-size:11px;}@media screen and (max-width:600px){.cart-items-wrapper{margin-top:70px;}}.cart-item .cart-image {width: 35%;}@media(max-width: 619px) {.cart-item .cart-image {width: 40%;}}.cart-item .cart-content p.product-title {text-transform: uppercase; font-size: 10px;}.cart-item .cart-content p.product-variant {font-size: 10px; margin-bottom: 23px; text-transform: uppercase; font-family: "light20"; margin-top: 14px;}.cart-item .cart-content span.product-price-main {width: 100%; display: block; float: none !important; text-align: left !important; text-transform: uppercase; font-family: "med20";}.cart-item .cart-content span.product-price-main span.strike {font-size: 10px;}.cart-item .cart-content span.product-price-main span.product-price.red {font-size: 10px; color: #000 !important; padding: 0 !important;}.cart-items-wrapper {border-top: 1px solid #000; flex: 1; display: flex; flex-direction: column; overflow-y: scroll;}.cart-item .cart-image img {width: 100%;}.cart-items-wrapper .cart-subtotal-wrapper {margin-right: 25px; margin-left: 25px; margin-bottom: 0;}.cart-items-wrapper .cart-subtotal-wrapper span.cart-subtotal {margin-left: 0; font-size: 12px;}.cart-items-wrapper .cart-subtotal-wrapper span.cart-subtotal-amount {margin-right: 0; font-family: "light20"; font-size: 12px;}.cart-checkout-button a {font-size: 12px; padding: 0; height: 100%; display: flex; justify-content: center; align-items: center; font-family: "med25"; font-style: normal; font-weight: 500; line-height: normal; text-transform: uppercase;}.checkout-wrapper .cart-checkout-button {display: flex; flex-wrap: wrap; align-items: center;}.cart-quantity-adjuster{margin-bottom: 25px;}.cart-item .cart-content .cart-quantity-adjuster span.toggle-quantity {background-color: transparent !important; padding: 0 !important; font-family: "light20"; font-size: 10px;}.cart-item .cart-content .cart-quantity-adjuster span {border: none !important; font-family: "light20"; font-size: 10px; }.cart-top-header.centered {margin-left: 25px; margin-right: 25px; display: flex; flex-direction: column;}.cart-top-header.centered p.drawer1-header {text-align: left; font-size: 12px; font-family: "med25"; padding-top: 28px;font-weight: normal;}.drawer1.drawer1-open svg.closer {right: 15px; top: 23px !important; left: auto; font-size: 25px;}.cart-top-header.centered span#to-free-ship {margin-top: 8px; display: block; font-size: 12px; font-family: "light20";}@media(max-width: 619px){ .cart-top-header.centered span#to-free-ship { font-size: 10px;}}.cart-top-header.centered .progress-container {margin-top: 5px; margin-bottom: 24px;}.cart-items-wrapper div .cart-item:last-child {border-bottom: none;}.cart-section-bottom {position: sticky;z-index: 999;background-color: white;bottom: 0px;border-top: 1px solid #000;border-top: 1px solid #000;}.cart-item .cart-content p.product-title a{white-space: normal;}.cart-items-wrapper .cart-subtotal-wrapper{margin-right: 15px;margin-left: 15px;padding-top: 20px;}.cart-top-header.centered{margin-left: 15px; margin-right: 15px;}.cart-top-header.centered .progress-container{width: 100%;}.cart-items-wrapper{margin-top: 0;}.cart-item .cart-content {flex: 1;padding-left: 16px; position: initial;}@media(max-width: 619px){.cart-item .cart-content {padding-left: 12px;}}.cart-empty-content{padding: 25px 15px; overflow-y: scroll; text-align: center; font-family: med20;}.category-links{font-family: med25 !important; font-size: 12px; text-transform: uppercase; }@media(max-width: 619px){.cart-checkout-button {width: unset;}}</style><div class="cart-top-header centered"> <p class="drawer1-header"><span class="bag-label">Bag <span class="bag-count">({{item_count}})</span></span></p>{{#show_free_ship}}<p style="padding-top: 0px; padding-bottom: 0px; margin-top: 0px; margin-bottom: 0px;"><span id="to-free-ship"></span></p>';

  const restrictedProductHTML = `{{#restrictedProduct}}<div style="border: 1px solid black;font-size: 10px;padding-block: 1rem;margin-bottom: 1rem; font-family: 'light20'">${restrictedProductMessage}</div>{{/restrictedProduct}}`;

  if (activateFreeShip) {
    if (DK.freeship && DK.freeshipOvernight === null) {
      tpl +=
        `<style>.remove-button{color: #000; font-family: light20; font-size: 10px; font-style: normal; font-weight: 300; line-height: normal; text-decoration-line: underline; text-transform: uppercase; margin-bottom: 26px; display: flex;}.container{margin-top:15px; margin-bottom: 15px; text-align:center;}.progress-container::before{content:"";background:#cacaca;position:absolute;top:50%;left:0;transform:translateY(-50%);height:2px;width:100%;z-index:-1}.progress-container{display:flex;justify-content:space-between;position:relative;margin:0 auto;margin-bottom:30px;max-width:100%;}.progress{background:#000;position:absolute;top:50%;left:0;transform:translateY(-50%);height:4px;width:0%;z-index:-1;transition:0.4s ease;border-radius: 30px;}.circle{background:#cacaca;color:#cacaca;border-radius:50%;height:10px;width:10px;display:flex;align-items:center;justify-content:center;border:3px solid #cacaca;transition:.4s ease}.standardship,.promoship{position:absolute;top:-8px}.standardship{right:0}.standardship-text{position:absolute;top:12px;right:0px;font-size:10px;}.promoship-text{font-size:10px;position:absolute;top:12px;right:25%; transform: translateX(35%);}.promoship{right:0}.circle.active{border-color:#000;background-color:#000;color:#fff}</style><div class="container"><div class="progress-container"><div class="progress" id="progress"></div><div class="circle standardship"></div></div></div>${restrictedProductHTML}</div><div style="clear:both:"></div>`;
    } else if (DK.freeship == 75 && DK.freeshipOvernight == 100) {
      // 75 free ship 100 overnight free ship
      tpl +=
        `<style>.remove-button{color: #000; font-family: light20; font-size: 10px; font-style: normal; font-weight: 300; line-height: normal; text-decoration-line: underline; text-transform: uppercase; margin-bottom: 1rem; display: flex;}.container{margin-top:15px; margin-bottom: 15px; text-align:center;}.progress-container::before{content:"";background:#cacaca;position:absolute;top:50%;left:0;transform:translateY(-50%);height:2px;width:100%;z-index:-1}.progress-container{display:flex;justify-content:space-between;position:relative;margin:0 auto;margin-bottom:30px;max-width:100%;}.progress{background:#000;position:absolute;top:50%;left:0;transform:translateY(-50%);height:4px;width:0%;z-index:-1;transition:0.4s ease;border-radius: 30px;}.circle{background:#cacaca;color:#cacaca;border-radius:50%;height:10px;width:10px;display:flex;align-items:center;justify-content:center;border:3px solid #cacaca;transition:.4s ease}.standardship,.promoship{position:absolute;top:-8px}.standardship{right:25%}.standardship-text{position:absolute;top:12px;right:0px;font-size:10px;}.promoship-text{font-size:10px;position:absolute;top:12px;right:25%; transform: translateX(35%);}.promoship{right:0}.circle.active{border-color:#000;background-color:#000;color:#fff}</style><div class="container"><div class="progress-container"><div class="progress" id="progress"></div><div class="circle standardship"></div><div class="circle promoship"></div><div class="promoship-text">STANDARD</div><div class="standardship-text">OVERNIGHT</div></div></div>${restrictedProductHTML}</div><div style="clear:both:"></div>`;
    } else if (DK.freeship == 75 && DK.freeshipOvernight == 75) {
      // 75 overnight free ship
      tpl +=
        `<style>.remove-button{color: #000; font-family: light20; font-size: 10px; font-style: normal; font-weight: 300; line-height: normal; text-decoration-line: underline; text-transform: uppercase; margin-bottom: 1rem; display: flex;}.container{margin-top:15px;text-align:center;}.progress-container::before{content:"";background:#cacaca;position:absolute;top:50%;left:0;transform:translateY(-50%);height:2px;width:100%;z-index:-1}.progress-container{display:flex;justify-content:space-between;position:relative;margin:0 auto;margin-bottom:30px;max-width:100%;}.progress{background:#000;position:absolute;top:50%;left:0;transform:translateY(-50%);height:4px;width:0%;z-index:-1;transition:0.4s ease;border-radius: 30px;}.circle{background:#cacaca;color:#cacaca;border-radius:50%;height:10px;width:10px;display:flex;align-items:center;justify-content:center;border:3px solid #cacaca;transition:.4s ease}.standardship,.promoship{position:absolute;top:-8px}.standardship{right:50%}.standardship-text{position:absolute;top:8px;right:0px;font-size:10px;}.promoship-text{font-size:10px;position:absolute;top:8px;right:-14px}.promoship{right:0}.circle.active{border-color:#000;background-color:#000;color:#fff}</style><div class="container"><div class="progress-container"><div class="progress" id="progress"></div><div class="circle promoship"></div><div class="standardship-text"></div></div></div>${restrictedProductHTML}</div><div style="clear:both:"></div>`;
    } else if (DK.freeship == 100 && DK.freeshipOvernight == 100) {
      // 100 free ship
      tpl +=
        `<style>.remove-button{color: #000; font-family: light20; font-size: 10px; font-style: normal; font-weight: 300; line-height: normal; text-decoration-line: underline; text-transform: uppercase; margin-bottom: 1rem; display: flex;}.container{margin-top:15px;text-align:center;}.progress-container::before{content:"";background:#cacaca;position:absolute;top:50%;left:0;transform:translateY(-50%);height:2px;width:100%;z-index:-1}.progress-container{display:flex;justify-content:space-between;position:relative;margin:0 auto;margin-bottom:30px;max-width:100%;}.progress{background:#000;position:absolute;top:50%;left:0;transform:translateY(-50%);height:4px;width:0%;z-index:-1;transition:0.4s ease;border-radius: 30px;}.circle{background:#cacaca;color:#cacaca;border-radius:50%;height:10px;width:10px;display:flex;align-items:center;justify-content:center;border:3px solid #cacaca;transition:.4s ease}.standardship,.promoship{position:absolute;top:-8px}.standardship{right:0px}.standardship-text{position:absolute;top:8px;right:0px;font-size:10px;}.promoship-text{font-size:10px;position:absolute;top:8px;right:-14px}.promoship{right:0}.circle.active{border-color:#000;background-color:#000;color:#fff}</style><div class="container"><div class="progress-container"><div class="progress" id="progress"></div><div class="circle standardship"></div><div class="standardship-text"></div></div></div>${restrictedProductHTML}</div><div style="clear:both:"></div>`;
    } else if (DK.freeship == 100 && DK.freeshipOvernight == 125) {
      // 100 free ship 125 overnight free ship
      tpl +=
        `<style>.remove-button{color: #000; font-family: light20; font-size: 10px; font-style: normal; font-weight: 300; line-height: normal; text-decoration-line: underline; text-transform: uppercase; margin-bottom: 1rem; display: flex;}.container{margin-top:15px; margin-bottom: 15px; text-align:center;}.progress-container::before{content:"";background:#cacaca;position:absolute;top:50%;left:0;transform:translateY(-50%);height:2px;width:100%;z-index:-1}.progress-container{display:flex;justify-content:space-between;position:relative;margin:0 auto;margin-bottom:30px;max-width:100%;}.progress{background:#000;position:absolute;top:50%;left:0;transform:translateY(-50%);height:4px;width:0%;z-index:-1;transition:0.4s ease;border-radius: 30px;}.circle{background:#cacaca;color:#cacaca;border-radius:50%;height:10px;width:10px;display:flex;align-items:center;justify-content:center;border:3px solid #cacaca;transition:.4s ease}.standardship,.promoship{position:absolute;top:-8px}.standardship{right:20%}.standardship-text{position:absolute;top:12px;right:0px;font-size:10px;}.promoship-text{font-size:10px;position:absolute;top:12px;right:25%; transform: translateX(35%);}.promoship{right:0}.circle.active{border-color:#000;background-color:#000;color:#fff}</style><div class="container"><div class="progress-container"><div class="progress" id="progress"></div><div class="circle standardship"></div><div class="circle promoship"></div><div class="promoship-text">STANDARD</div><div class="standardship-text">OVERNIGHT</div></div></div>${restrictedProductHTML}</div><div style="clear:both:"></div>`;
    } else if (DK.freeship == 50 && DK.freeshipOvernight == 100) {
      // 50 free ship 100 overnight free ship
      tpl +=
        `<style>.remove-button{color: #000; font-family: light20; font-size: 10px; font-style: normal; font-weight: 300; line-height: normal; text-decoration-line: underline; text-transform: uppercase; margin-bottom: 1rem; display: flex;}.container{margin-top:15px; margin-bottom: 15px; text-align:center;}.progress-container::before{content:"";background:#cacaca;position:absolute;top:50%;left:0;transform:translateY(-50%);height:2px;width:100%;z-index:-1}.progress-container{display:flex;justify-content:space-between;position:relative;margin:0 auto;margin-bottom:30px;max-width:100%;}.progress{background:#000;position:absolute;top:50%;left:0;transform:translateY(-50%);height:4px;width:0%;z-index:-1;transition:0.4s ease;border-radius: 30px;}.circle{background:#cacaca;color:#cacaca;border-radius:50%;height:10px;width:10px;display:flex;align-items:center;justify-content:center;border:3px solid #cacaca;transition:.4s ease}.standardship,.promoship{position:absolute;top:-8px}.standardship{right:50%}.standardship-text{position:absolute;top:12px;right:0px;font-size:10px;}.promoship-text{font-size:10px;position:absolute;top:12px;right:50%; transform: translateX(35%);}.promoship{right:0}.circle.active{border-color:#000;background-color:#000;color:#fff}</style><div class="container"><div class="progress-container"><div class="progress" id="progress"></div><div class="circle standardship"></div><div class="circle promoship"></div><div class="promoship-text">STANDARD</div><div class="standardship-text">OVERNIGHT</div></div></div>${restrictedProductHTML}</div><div style="clear:both:"></div>`;
    }  else if (DK.freeship == 50 && DK.freeshipOvernight == 75) {
      // 50 free ship 100 overnight free ship
      tpl +=
        `<style>.remove-button{color: #000; font-family: light20; font-size: 10px; font-style: normal; font-weight: 300; line-height: normal; text-decoration-line: underline; text-transform: uppercase; margin-bottom: 1rem; display: flex;}.container{margin-top:15px; margin-bottom: 15px; text-align:center;}.progress-container::before{content:"";background:#cacaca;position:absolute;top:50%;left:0;transform:translateY(-50%);height:2px;width:100%;z-index:-1}.progress-container{display:flex;justify-content:space-between;position:relative;margin:0 auto;margin-bottom:30px;max-width:100%;}.progress{background:#000;position:absolute;top:50%;left:0;transform:translateY(-50%);height:4px;width:0%;z-index:-1;transition:0.4s ease;border-radius: 30px;}.circle{background:#cacaca;color:#cacaca;border-radius:50%;height:10px;width:10px;display:flex;align-items:center;justify-content:center;border:3px solid #cacaca;transition:.4s ease}.standardship,.promoship{position:absolute;top:-8px}.standardship{right:45%}.standardship-text{position:absolute;top:12px;right:0px;font-size:10px;}.promoship-text{font-size:10px;position:absolute;top:12px;right:45%; transform: translateX(35%);}.promoship{right:0}.circle.active{border-color:#000;background-color:#000;color:#fff}</style><div class="container"><div class="progress-container"><div class="progress" id="progress"></div><div class="circle standardship"></div><div class="circle promoship"></div><div class="promoship-text">STANDARD</div><div class="standardship-text">UPGRADED/OVERNIGHT</div></div></div>${restrictedProductHTML}</div><div style="clear:both:"></div>`;
    }
  } else {
    tpl +=
      `<style>.remove-button{color: #000; font-family: light20; font-size: 10px; font-style: normal; font-weight: 300; line-height: normal; text-decoration-line: underline; text-transform: uppercase; margin-bottom: 1rem;display: flex;}.container{margin-top:15px;text-align:center;}.progress-container::before{content:"";background:#cacaca;position:absolute;top:50%;left:0;transform:translateY(-50%);height:2px;width:100%;z-index:-1}.progress-container{display:flex;justify-content:space-between;position:relative;margin:0 auto;margin-bottom:30px;max-width:100%;}.progress{background:#000;position:absolute;top:50%;left:0;transform:translateY(-50%);height:4px;width:0%;z-index:-1;transition:0.4s ease;border-radius: 30px;}.circle{background:#cacaca;color:#cacaca;border-radius:50%;height:10px;width:10px;display:flex;align-items:center;justify-content:center;border:3px solid #cacaca;transition:.4s ease}.standardship,.promoship{position:absolute;top:-8px}.standardship{right:0px}.standardship-text{position:absolute;top:8px;right:-30px}.circle.active{border-color:#000;background-color:#000;color:#fff}</style><div class="container"><div class="progress-container"><div class="progress" id="progress"></div><div class="circle standardship"></div><div class="standardship-text"></div></div></div>${restrictedProductHTML}</div><div style="clear:both:"></div>`;
  }

  tpl +=
    `{{/show_free_ship}}
    {{^show_free_ship}}
    <p></p>
    {{/show_free_ship}}
    </div>
    <div class="cart-items-wrapper">
        {{#show_categories}}
        <div class="cart-empty-content">
            <p>YOUR BAG IS EMPTY</p>
            {{#categories}}
            <button onclick="window.location.href='{{link}}'" class="category-links">{{name}}</button>
            <br/>
            {{/categories}}
        </div>
        {{/show_categories}}
        {{^show_categories}}
        <div data-test-id="cart--item__container" class="cart-items-contiainer">
            {{#gwp_valid}}
              <style>
                .gwp-container {
                  display: block;
                  padding: 16px 10%;
                }
                .gwp-container .gwp-content {
                  display: flex;
                  align-items: center;
                  justify-content: flex-start;
                  border: 1px dashed #000;
                  padding: 14px;
                }
                .gwp-container img {
                  width: 116px;
                  height: 138px;
                  object-fit: cover;
                  object-position: 0px -25px;
                }
                .gwp-container p { 
                  font-family: med20;
                  font-size: 12px;
                  font-weight: 500;
                  line-height: 1.2;
                  text-transform: uppercase;
                  margin-bottom: 10px;
                  margin-top: 0px;
                }
                .gwp-container button {
                  border: 1px solid #000;
                  color: #000;
                  width: 110px;
                  height: 32px;
                  font-family: med25 !important; 
                  font-size: 10px;
                  background: #fff;
                }
                .gwp-container button:hover {
                  cursor: pointer;
                  background: #000;
                  color: #fff;
                }
                .gwp-container .gwp-content-text {
                  display: flex;
                  flex-direction: column;
                  align-items: start;
                  justify-content: center;
                  margin-left: 23px;
                }
                .gwp-button {
                  position: relative;
                }
                .gwp-button.loading {
                  pointer-events: none;
                  opacity: 0.7;
                }
                .gwp-button .spinner {
                  display: none;
                  width: 16px;
                  height: 16px;
                  border: 2px solid #000000;
                  border-top: 2px solid transparent;
                  border-radius: 50%;
                  position: absolute;
                  left: 50%;
                  top: 50%;
                  transform: translate(-50%, -50%);
                  animation: spin 1s linear infinite;
                }
                .gwp-button.loading .spinner {
                  display: block;
                }
                .gwp-button.loading span {
                  visibility: hidden;
                }
                @keyframes spin {
                  0% { transform: translate(-50%, -50%) rotate(0deg); }
                  100% { transform: translate(-50%, -50%) rotate(360deg); }
                }
              </style>
              <div class="gwp-container">
                <div class="gwp-content">
                  <img class="gwp-image" src="{{gwp_settings.gwp_product_image}}" alt="Free Gift Product Image"/>
                  <div class="gwp-content-text">
                    <p class="gwp-title">YOU'VE EARNED A FREE GIFT</p>
                    <p class="gwp-product-title">{{gwp_settings.gwp_product_title}}</p>
                    <button 
                      class="gwp-button"
                      onclick="this.classList.add('loading'); DK.addToCart({{gwp_settings.gwp_product_variant_id}}, function(){DK.renderCart(); }, '', {}, null)">
                      <span>ADD</span>
                      <div class="spinner"></div>
                    </button>
                  </div>
                </div>
              </div>
            {{/gwp_valid}}
            {{#items}}
            <div data-test-id="cart--item" data-variant-id="{{ id }}" data-product-type="{{ product_type }}" class="cart-item {{#visible}}cart-item--dotted{{/visible}}">
                <div class="cart-image">
                    {{#loyalty_free_gift}}
                      <img src="{{image}}"/>
                    {{/loyalty_free_gift}}
                    {{^loyalty_free_gift}}
                      <a href="/products/{{handle}}">
                        <img src="{{image}}"/>
                      </a>
                    {{/loyalty_free_gift}}
                </div>
                <div class="cart-content">
                    <p data-test-id="cart--item__title" class="product-title">
                        <span style="overflow: hidden; text-overflow: ellipsis; width: 86%; display: inline-block; white-space: nowrap;">
                        {{#loyalty_free_gift}}
                          {{product_title}}
                        {{/loyalty_free_gift}}
                        {{^loyalty_free_gift}}
                            <a style="text-decoration:none;color:black;" href="/products/{{handle}}">{{product_title}}</a>
                        {{/loyalty_free_gift}}
                        </span>
                    </p>
                    {{#is_not_gift_card}}<p data-test-id="cart--item__variant" class="product-variant" data-vid="{{id}}">{{variant_title}}</p>{{/is_not_gift_card}}
                    <p class="tw-font-[med20] tw-uppercase tw-text-[10px]">{{ selling_plan_allocation.selling_plan.name }}</p>
                    <p class="button_remove_item">
                        {{#loyalty_free_gift}}
                          <span class="tw-flex tw-w-fit tw-font-[light25] tw-font-medium tw-text-[10px]" style="margin-bottom: 30px"></span>
                        {{/loyalty_free_gift}}
                        {{^loyalty_free_gift}}
                          <div class="cart-quantity-adjuster">
                              <span data-test-id="cart--item__inc" style="background-color: #e5e2e2; padding: 5px 10px; cursor: pointer;" class="toggle-quantity" data-id="{{id}}" data-type="remove" data-cart-key="{{ key }}" data-current-qty="{{quantity}}">-</span>
                              <span data-test-id="cart--item__qty" style="border: 1px solid #d9d3d3; padding: 4px 10px;">{{quantity}}</span>
                              <span data-test-id="cart--item__dec" style="background-color: #e5e2e2; padding: 5px 10px; cursor: pointer;" class="toggle-quantity" data-parent-id="{{product_id}}" data-id="{{id}}" data-cart-key="{{ key }}" data-type="add" data-current-qty="{{quantity}}">+</span>
                          </div>
                        {{/loyalty_free_gift}}
                        <div>
                            <a data-test-id="cart--item__remove" class="toggle-quantity hover remove-button" data-type="force-remove" data-quantity="{{quantity}}" data-id="{{id}}" data-cart-key="{{ key }}">REMOVE</a>
                        </div>
                        <span data-test-id="cart--item__price" style="margin-bottom: 8px;" class="product-price-main product-price-main--{{id}}">
                            {{^loyalty_free_gift}}
                              {{#properties.strike}}
                                <span data-test-id="cart--item__compare" class="strike{{#loyalty_product_in_cart}} tw-font-[med20]{{/loyalty_product_in_cart}}">   
                                  {{properties.compare_at_price}}
                                </span>
                              {{/properties.strike}}
                              {{^properties.strike}}
                                {{#show_discount_strike_through}}
                                  <span class="strike{{#loyalty_product_in_cart}} tw-font-[med20]{{/loyalty_product_in_cart}}" style="padding-right: 4px;">   
                                    {{discount_strike_through_price_display}}
                                  </span>
                                {{/show_discount_strike_through}}
                              {{/properties.strike}}
                            {{/loyalty_free_gift}}
                            {{#display_loyalty_pricing}}
                                <span class="{{#loyalty_product_in_cart}}strike tw-font-[med20]{{/loyalty_product_in_cart}} tw-text-[10px] tw-mt-[2px]" {{#properties.strike}}style="margin-left: 4px;"{{/properties.strike}}>
                                  {{properties.price}}
                                </span>
                            {{/display_loyalty_pricing}}
                            {{^display_loyalty_pricing}}
                              <span data-test-id="cart--item__current" class="product-price" style="font-size: 10px; margin-top: 2px;">
                                  {{^loyalty_free_gift}}{{#properties.strike}}&nbsp;&nbsp;{{/properties.strike}}{{/loyalty_free_gift}}{{formatted_final_line_price}}
                              </span>
                            {{/display_loyalty_pricing}}
                        </span>
   
                        {{#display_loyalty_pricing}}
                          <span class="tw-block tw-w-fit tw-font-[\'med20\'] tw-font-medium tw-text-[10px] tw-mb-[15px]" style="color: {{ loyalty_pricing_text_color }}; background-color: {{ loyalty_pricing_bg_color }}">
                          {{#loyalty_product_in_cart}}
                          {{formatted_final_line_price}} &nbsp; {{loyalty_percent_off}}% Off Applied
                          {{/loyalty_product_in_cart}}
                          {{^loyalty_product_in_cart}}
                          or {{ loyalty_discount }} & Earn {{ loyalty_cashback }} w/&nbsp; 
                          <span class="tw-text-white tw-bg-stash-green tw-px-1 tw-font-[med25]">Stash</span>
                          {{/loyalty_product_in_cart}}
                          </span>
                        {{/display_loyalty_pricing}}

                        {{#promo_price_text}}
                          {{^loyalty_free_gift}}
                            <p class="cart-promo-price tw-text-[var(--promo-code-color)] tw-bg-[var(--promo-code-background-color)] tw-w-fit">{{promo_price_text}}</p>
                          {{/loyalty_free_gift}}
                        {{/promo_price_text}}

                        {{^loyalty_free_gift}}
                          {{#discounts}}
                            <p class="tw-w-fit" style="font-family: light20; font-size: 9px; margin-bottom: 4px;">{{title}} (-{{amount}})</p>
                          {{/discounts}}
                        {{/loyalty_free_gift}}
                    </p>
                </div>
            </div>
            {{/items}}
              {{#loyalty_product}}
                {{#show_loyalty_product_in_cart}}
                  <style>
                    .loyalty-details--container {
                      position: relative;
                      padding: 16px;
                    }
                    .loyalty-details--container::before {
                        content: '';
                        background-image: linear-gradient(to right, #000 15%, rgba(255, 255, 255, 0) 15%);
                        background-position: bottom;
                        background-size: 9px 2px;
                        background-repeat: repeat-x;
                        position:absolute;
                        top: 0;
                        left: 16px;
                        right: 16px;
                        height: 1px;
                      }
                      .loyalty-details--container::after {
                        content: '';
                        background-image: linear-gradient(to right, #000 15%, rgba(255, 255, 255, 0) 15%);
                        background-position: bottom;
                        background-size: 9px 2px;
                        background-repeat: repeat-x;
                        position:absolute;
                        bottom: 0;
                        left: 16px;
                        right: 16px;
                        height: 1px;
                      }
                  </style>
                  <div class="loyalty-details--container tw-p-4">
                    <style>
                      .loyalty__cta {
                        cursor: pointer;
                        padding: 10px 26px;
                        transition: background-color 0.3s, color 0.3s;
                        background-color: #fff;
                        border: 0;
                        color: black;
                        width: 105px;
                      }
                      .loyalty__cta:hover {
                        background-color: #2d2d2d;
                        color: #fff;
                      }
                    </style>
                    <div id="loyalty_details">
                      <div class="tw-list-none">
                        <div class="tw-flex tw-justify-between tw-flex-row tw-mb-4">
                          <h1 style="font-size:20px" class="max-md:tw-text-[17px] tw-my-0 tw-text-black tw-font-medium">
                            <span class="tw-flex tw-gap-1 tw-items-center tw-flex-wrap med20">{{{ loyalty_mini_cart_promo_title }}}</span>
                          </h1> 
                          <button 
                            data-loyalty-product="{{loyalty_product}}" 
                            onclick="DK.addToCart({{loyalty_product}}, function(){DK.renderCart()}, '{{loyalty_product_tags}}', {_inveterate_subscription: true}, {{loyalty_product_selling_plan_id}})" 
                            class="loyalty__cta tw-font-[med25] tw-text-[10px] tw-font-medium tw-border-[1px] tw-border-black tw-border-solid">
                              ADD
                          </button>
                        </div>
                      </div>
                      <div class="rte tw-text-black tw-font-[med20] tw-text-[12px] tw-leading-4 tw-h-full tw-overflow-hidden tw-transition-all">
                        <table class="tw-w-full">
                          <tr class="tw-font-[med25] tw-text-[10px] tw-uppercase">
                            <th class="tw-text-left tw-pb-[6px] tw-font-normal">
                              <span class="tw-relative tw-cursor-pointer">
                                Benefits
                              </span>
                            </th>
                            <th class="tw-text-center tw-pb-[2px] tw-font-normal">Member</th>
                            <th class="tw-text-center tw-pb-[2px] tw-font-normal">Non-Member</th>
                          </tr>
                          {{#loyalty_mini_cart_promo_value_prop}}
                            <tr class="tw-font-[med20] tw-text-[12px]" style="line-height: 1.8;">
                              <td class="tw-text-left">{{.}}</td>
                              <td class="tw-text-center" style="color: {{stash_text_background_color}}"><i class="fa fa-check"></i></td>
                              <td class="tw-text-center" style="color: #7D7D7D"><i class="fa fa-times" style="color: #7D7D7D"></i></td>
                            </tr>
                          {{/loyalty_mini_cart_promo_value_prop}}
                        </table>
                      </div>
                    </div>
                    <div class="tw-flex tw-flex-row tw-justify-start tw-items-center tw-pt-4 tw-font-[med20]">
                      <span class="tw-text-[9px] tw-text-[#5c5f62]" style="font-family: light20; line-height: 1.6;">
                        {{loyalty_mini_cart_disclaimer_text}} 
                        <a href="{{loyalty_mini_cart_disclaimer_link}}" class="tw-text-[9px]" style="font-family: light20; color: #5c5f62 !important;">{{loyalty_mini_cart_disclaimer_link_text}}</a>
                      </span>
                    </div>
                  </div>
                {{/show_loyalty_product_in_cart}}
              {{/loyalty_product}}
        </div>`;

  //  Carousel complete your order
  var upsellProductsJSON =
    typeof $("script[raw-upsell-product-json]").html() !== "undefined"
      ? JSON.parse($("script[raw-upsell-product-json]").html())
      : "";
  //  Filter upsell product
  var responseVariantIds = new Set();
  response.items.forEach((product) => {
    responseVariantIds.add(product.variant_id);
  });

  var setupSliderCartUpsell = false;

  if (upsellProductsJSON.length > 0) {
    response.upsell_products = upsellProductsJSON;

    response.upsell_products = response.upsell_products.filter((product) => {
      if (product.available) {
        return !product.variants.some((variant) => responseVariantIds.has(variant.id));
      }
      return false;
    });

    //  Formatting all products prices

    $.each(response.upsell_products, function (i, v) {
      response.upsell_products[i].price = formatPrice(v.price);
      response.upsell_products[i].price_text = DKi18nPriceFormatter(v.price * 100, {formatOnly: true});
      response.upsell_products[i].compare_at_price = formatPrice(v.compare_at_price);
      response.upsell_products[i].compare_at_price_text = DKi18nPriceFormatter(
        v.compare_at_price * 100,
        {formatOnly: true}
      );
      response.upsell_products[i].compare_at_price_max = formatPrice(v.compare_at_price_max);
      response.upsell_products[i].compare_at_price_min = formatPrice(v.compare_at_price_min);
      response.upsell_products[i].price_max = formatPrice(v.price_max);
      response.upsell_products[i].price_min = formatPrice(v.price_min);

      //  Variant price computation
      $.each(v.variants, function (j, variant) {
        response.upsell_products[i].variants[j].price = formatPrice(variant.price);
        response.upsell_products[i].variants[j].price_text = DKi18nPriceFormatter(variant.price * 100, {formatOnly: true});
        response.upsell_products[i].variants[j].compare_at_price = formatPrice(
          variant.compare_at_price
        );
        response.upsell_products[i].variants[j].compare_at_price_text = DKi18nPriceFormatter(
          variant.compare_at_price * 100,
          {formatOnly: true}
        );
      });

      response.upsell_products[i].strike = false;
      if (parseFloat(response?.upsell_products?.[i]?.price || 0) < parseFloat(response?.upsell_products?.[i]?.compare_at_price || 0))
        response.upsell_products[i].strike = true;
    });

    var upsell_title = $("script[upsell-title]").html();
    if (response.upsell_products.length > 0) {
      tpl +=
        '<div data-test-id="cart--upsells" id="complete-look" style="display: block; background-color: #fff;"> <div class="carousel upsell-carousel"> <div class="carousel-head"style="font-size: 12px; font-weight: bolder; margin-left: 20px;text-transform: uppercase;"></div><div class="upsell-title">' +
        upsell_title +
        //  Carousel
        '</div><div class="hrow slider"><div id="complete-look-items" class="scroller sliding slider-pw" data-carousel-id="889">' +
        '{{#upsell_products}}{{#available}}<div data-id="{{id}}" data-mpid="{{tags.parentId}}" class="product-card upsell-product-card" data-compare-at-price="{{compare_at_price}}" data-price="{{price}}"><div class="card-actions"><div class="card-actions-sale" data-pid="{{id}}"></div><div class="card-actions-fav" data-pid="{{id}}"></div></div><div class="card-content"><div class="image-holder loading"><a href="/products/{{handle}}"><img data-id="{{id}}" data-card-status="0" data-src="{{featured_image}}" src="{{featured_image}}" alt="" class="grid-image" loading="lazy" fetchpriority="low" data-src2="{{featured_image}}"></a>';

      tpl +=
        '</div><div class="content-holder mobile-mt6"><div style="display:flex;justify-content:space-between;"><p class="product-title"><a href="/products/{{handle}}">{{title}}</a></p></div><div class="price-container"><p class="product-price">{{price_text}}</p>{{#strike}}<p class="strike">{{compare_at_price_text}}</p>{{/strike}}</div><p class="upsell-promo-price" data-tags="{{tags}}" data-currency="{{currency_symbol}}" data-price="{{price}}"></p></div><button class="upsell-add" data-product-id="{{variants.0.id}}" onclick="DK.addToCart({{variants.0.id}}, function(){DK.renderCart()}, `{{tags}}`)">ADD</button></div></div>{{/available}}{{/upsell_products}}</div></div></div></div></div>';
      setupSliderCartUpsell = true;
      //  END Carousel
    }
  } else {
    tpl += "</div>";
  }
  DK.openStashDrawer = () => {
    const stash_accordion = document.getElementById("join_stash_accordion");
    const loyalty_chevron = document.getElementById("loyalty_chevron");
    const info_container = document.querySelector("[data-info-container]");
    loyalty_chevron.classList.toggle("tw-rotate-180");
    if (stash_accordion.style.maxHeight === "0px") {
      stash_accordion.style.maxHeight = stash_accordion.scrollHeight + "px";
      stash_accordion.style.marginBottom = ".75rem";
    } else {
      stash_accordion.style.maxHeight = "0px";
      stash_accordion.style.marginBottom = "0px";
      if (info_container.style.maxHeight !== "0px") {
        DK.openInfoDrawer();
      }
    }
  }
  DK.openInfoDrawer = () => {
    const info_container = document.querySelector("[data-info-container]");
    if (info_container.style.maxHeight === "0px") {
      info_container.style.maxHeight = info_container.scrollHeight + "px";
      info_container.style.padding = "20px 28px"
    } else if (info_container.style.maxHeight !== "0px") {
      info_container.style.maxHeight = "0px";
      info_container.style.padding = "0px 28px"
    }
  }

  // Purple Dot required change
  // ensure shopper directed to /cart if there is a pre-order item in cart
  var hasPurpleDotPreorderItems = false;
  for (const item of response.items) {
    if (item.properties.some(property => property[0] === "__releaseId")) {
      hasPurpleDotPreorderItems = true;
      break;
    }
  }
  response.has_purple_dot_preorder_items = hasPurpleDotPreorderItems;

  //  Bottom Section with checkout button
  tpl +=
    `<div class="cart-section-bottom {{#show_cash_back}}tw-border-0{{/show_cash_back}} {{#show_loyalty_product_in_cart}} tw-border-0{{/show_loyalty_product_in_cart}}">
      {{#show_cash_back}}
        <div class="stash-cash-container tw-px-4 tw-text-white tw-flex tw-justify-between tw-font-[med20] tw-text-xs tw-items-center">
          <style>
            .stash-cash-container {
              padding-top: 12px;
              padding-bottom: 12px;
              background-color: {{stash_text_background_color}};
            }
          </style>
          <span>Cash Back Earned W/ Stash</span><span>{{loyalty_cashback_amount}}</span>
        </div>
      {{/show_cash_back}}
      <div class="cart-subtotal-wrapper {{^show_cash_back}}tw-border-t-[1px] tw-border-b-0 tw-border-x-0 tw-border-black tw-border-solid{{/show_cash_back}}">
        {{#showSubtotalWithoutCode}}
          <div class="tw-flex tw-justify-between tw-flex-row tw-w-full tw-flex-wrap"> 
              <span class="cart-subtotal tw-font-[med20] tw-font-medium">Subtotal</span>
              <span data-test-id="cart--subtotal" class="cart-subtotal-amount tw-font-[med20] tw-font-medium" data-subtotal_currency="{{currency_symbol}}" data-subtotal_price="{{items_subtotal_price}}" >
                {{items_subtotal_price_text}}
              </span>
          </div>
        {{/showSubtotalWithoutCode}}
        {{#showSubtotalWithCode}}
          {{#has_promo_items}}
            <div id="subtotal-with-promo" class="tw-flex tw-justify-between tw-font-[med20] tw-text-[10px]" data-subtotal"">
              <span class="tw-text-[var(--promo-code-color)] tw-bg-[var(--promo-code-background-color)]">
                Total w/code: {{active_promo_code}}
              </span>
              <span class="tw-mt-[-1px]">
                {{items_subtotal_promo_price}}
              </span>
            </div>
          {{/has_promo_items}}
          {{^has_promo_items}}
            <div class="tw-flex tw-justify-between tw-flex-row tw-w-full tw-flex-wrap"> 
              <span class="cart-subtotal tw-font-[med20] tw-font-medium">Subtotal</span>
              <span class="cart-subtotal-amount tw-font-[med20] tw-font-medium" data-subtotal_currency="{{currency_symbol}}" data-subtotal_price="{{items_subtotal_price}}" >
                {{items_subtotal_price_text}}
              </span>
            </div>
          {{/has_promo_items}}
        {{/showSubtotalWithCode}}
      </div>
      <div id="subtotal-with-promo" data-subtotal""></div>
        <div class="checkout-wrapper">
          <div class="cart-checkout-button">
            <a 
              {{#has_purple_dot_preorder_items}}href="/cart"{{/has_purple_dot_preorder_items}}
              {{^has_purple_dot_preorder_items}}href="/checkout/"{{/has_purple_dot_preorder_items}}
              type="submit" id="checkout" name="checkout" form="cart" data-test-id="cart--checkout">Checkout</a>
          </div>
        </div>
      </div>
    {{/show_categories}}</div>`;
  var H = Hogan.compile(tpl);

  var adjustedCartCount = 0;

  $.each(response.items, function (index, item) {
    if (item.product_type !== "Insurance") {
      item["visible"] = true;
      adjustedCartCount += item.quantity;
    }
  });
  response.item_count = adjustedCartCount;
 
  const { showSubtotalWithCode, showSubtotalWithoutCode } = discountSettings;

  if (typeof showSubtotalWithCode !== "undefined" || typeof showSubtotalWithoutCode !== "undefined") {
    response.showSubtotalWithCode = showSubtotalWithCode;
    response.showSubtotalWithoutCode = showSubtotalWithoutCode;
  } else {
    response.showSubtotalWithCode = false;
    response.showSubtotalWithoutCode = true;
  }

  response.items = response.items.map((item) => {
    const discounts = item.discounts.map((_, index) => {
      return { 
        amount: DKi18nPriceFormatter(item.discount_allocations[index].amount, { formatOnly: true }), 
        title: item.discount_allocations[index].discount_application.title.toUpperCase()
      }
    });

    return { ...item, discounts  };
  });
  
  const gwpSettings = JSON.parse(document.querySelector("[data-gwp-settings]").innerHTML);
  response.gwp_settings = gwpSettings;
  response.gwp_valid =  gwpSettings?.gwp_enabled &&
    (gwpSettings?.gwp_trigger_type == "threshold" && response.total_price > (gwpSettings?.gwp_trigger_threshold * 100)) &&
    gwpSettings?.gwp_product_variant_id &&
    gwpSettings?.gwp_product_available == true && 
    !response.items.some((item) => item.id == gwpSettings?.gwp_product_variant_id) && 
    Shopify.country === "US"

  // move any items with the "gwp-gift-product" tag to the top of the cart
  response.items = response.items.sort((a, b) => {
    if (a.tags.includes("gwp-gift-product")) {
      return -1;
    }
    return 1;
  });

  // render cart
  var html = H.render(response);

  $(".drawer1").html(html);
  (async () => {
    const inventory = await Promise.all(
      response.items.map(async ({ product_id, variant_id }) => {
        const response = await fetch(`https://xz.dollsmktg.com/api/nprod/${product_id}`);

        try {
          const { data } = await response.json();
          const variantMatch = data?.sizes?.find(({ variant_id: size_id }) => size_id === variant_id);
          return { inventory: variantMatch?.inventory, variantID: variantMatch?.variant_id };
        } catch (error) {
          console.error(error);
          return { inventory: null, variantID: variant_id };
        }
      })
    );

    const allCartItemElements = document.querySelectorAll(".cart-items-contiainer .cart-item");

    allCartItemElements.forEach((element) => {
      const match = inventory.find(
        ({ variantID }) => variantID === Number(element.dataset.variantId)
      );
    });
  })();

  if (setupSliderCartUpsell && $("#complete-look-items").length) {
    setupSlide("#complete-look-items");
  }

  if (Shopify.country !== "US") {
    $("#to-free-ship").hide();
    $(".progress-container").hide();
  }

  DK.addToCart;

  var $cartItemLast = $(".cart-item--dotted").last();
  $cartItemLast.removeClass("cart-item--dotted");

  for (var rr in response.items) {
    _property = response.items[rr].properties;
    if (typeof _property != "undefined" && typeof _property == "object") {
      if (typeof _property.preorder != "undefined" && typeof _property.preorder == "string") {
        var _myid = response.items[rr].id;
        var preorderHtml =
          "<p style='font-size: 11px; color: red'>Estimated shipping date: " +
          _property.preorder +
          "</p>";
        $(preorderHtml).insertAfter(".product-variant[data-vid=" + _myid + "]");
        $(".product-variant[data-vid=" + _myid + "]").addClass("vshorter");
      }
      if (typeof _property.compare_at_price == "number" && typeof _property.op == "number") {
        if (_property.compare_at_price > _property.op) {
          var fifty = _property.compare_at_price / 2;
          if (fifty >= _property.op) {
            /*
              var _myid = response.items[rr].id;
              var preorderHtml = "<p style='font-size: 11px; color: red'>Final Sale</p>";
              
              $(preorderHtml).insertAfter('.product-variant[data-vid='+_myid+']');
              $('.product-variant[data-vid='+_myid+']').addClass('vshorter');
              */
          }
        }
      }
    }
  }

  // for Final Sale message
  $.each(response.items, function (k, v) {
    var cartproductPrice = (parseFloat(v.final_price) / 100).toFixed(2);
    var cartgetDecimal = cartproductPrice.toString().split(".")[1];

    var vid = v.variant_id;

    if (
      cartgetDecimal == "98" &&
      $(`[data-variant-id=${vid}] p[data-final-sale-display]`).length === 0
    ) {
      var cartfinalPriceSection =
        "<p style='font-size:10px;display:block;text-transform:uppercase; font-family: light20; margin: 0;' data-final-sale-display>Final Sale</p>";
      $(cartfinalPriceSection).insertBefore(".product-price-main--" + vid);
    }
  });

  // for Purple Dot Pre-order message
  $.each(response.items, function (k, v) {
    let isPreorder = false;
    let estimatedShipDate;
    for (const [name, value] of v.properties) {
      if ((name === "Purple Dot Pre-order")) {
        isPreorder = true;
        estimatedShipDate = value;
      }
    }

    if (isPreorder) {
      const preorderElement = $("<p style='font-size:10px;display:block;text-transform:uppercase; font-family: med20; margin: 0;' data-pre-order-display>" + estimatedShipDate + "</p>");
      const cartContentElement = $(`[data-variant-id="${v.variant_id}"].cart-item .cart-content`);
      
      cartContentElement.append(preorderElement);
    }
  });

  filterDrawer(true);

  $("#shopping_bag_counter").html(response.item_count);

  var bmsm = false;
  if (bmsm && country == "US") {
    var current = response.items_subtotal_price;
    var threshold = 100;
    if (current >= 100 && current < 200) threshold = 200;
    if (current >= 200 && current < 300) threshold = 300;

    $(".animated-progress span").each(function (k, v) {
      if (k == 0) $(this).animate({ width: (current / threshold) * 100 + "%" }, 1000);
    });

    var tilnext = threshold - current;
    tilnext = Math.round(tilnext * 100) / 100;
    tilnext = parseFloat(tilnext).toFixed(2);

    var html = "";
    if (current >= 300) {
      html = "<br/>Get $100 off your order! Use code: DEALZ100";
    } else {
      if (current < 100) {
        html = "Add $" + tilnext + " to get $20 w/code DEALZ20";
      }
      if (current >= 100 && current < 200) {
        html = "Add $" + tilnext + " to get $50 w/code DEALZ50";
      }
      if (current >= 200 && current < 300) {
        html = "Add $" + tilnext + " to get $100 w/code DEALZ100";
      }
    }
    $("#to-free-ship").html(html);
    /*
        if(response.items_subtotal_price < 200){
          html += '<br/>Get $20 off when you spend $100! Use code: DEALZ20';
        }
        if(response.items_subtotal_price >= 200 && response.items_subtotal_price < 300){
          html += '<br/>Get $50 off when you spend $200! Use code: DEALZ50';
        }
        if(response.items_subtotal_price >= 300){
          html += '<br/>Get $100 off when you spend $300! Use code: DEALZ100';
        }
        */
  } else {
    var onlyStandard = DK.freeshipOvernight === null || DK.freeship == DK.freeshipOvernight;

    if (activateFreeShip && onlyStandard) {
      var progresswidth = (response.items_subtotal_price / DK.freeship) * 100;
      if (progresswidth > 100) progresswidth = 100;
      $("#progress").css("width", progresswidth + "%");
    } else if (activateFreeShip) {
      // 50% of the bar is designated for free standard ship.
      var totalwidth = $(".progress-container").width();
      var subtotalPrice = parseFloat(response.items_subtotal_price);

      var progresswidth = (subtotalPrice / DK.freeshipOvernight) * 100;
      if (progresswidth > 100) progresswidth = 100;
      $("#progress").css("width", progresswidth + "%");
    } 

    var leftForStandard = DK.freeship - response.items_subtotal_price;
    leftForStandard = Math.round(leftForStandard * 100) / 100;
    leftForStandard = parseFloat(leftForStandard).toFixed(2);

    var leftForOvernight = false;
    if (country == "US") {
      leftForOvernight = DK.freeshipOvernight - response.items_subtotal_price;
      leftForOvernight = Math.round(leftForOvernight * 100) / 100;
      leftForOvernight = parseFloat(leftForOvernight).toFixed(2);
    }

    var html = "";

    var leftForStandardDisplay = response.currency_symbol + leftForStandard;
    var leftForOvernightDisplay = response.currency_symbol + leftForOvernight;
    var standardThreholdEqualsOvernight = DK.freeship === DK.freeshipOvernight;

    switch (true) {
      case DK.freeshipOvernight === null && leftForStandard == DK.freeship:
        html += `ADD ${leftForStandardDisplay} FOR <span style="font-family: med20;">FREE STANDARD SHIPPING</span>`;
        break;
        
      case DK.freeshipOvernight === null && leftForStandard <= 0:
        html += `YOU'VE EARNED <span style="font-family: med20;">FREE STANDARD SHIPPING</span>`;        

        $(".standardship")
          .addClass("active")
          .html('<i style="font-size:9px" class="fa fa-check"></i>');

        break;

      case DK.freeshipOvernight === null && leftForStandard > 0:
        html += `YOU'RE ${leftForStandardDisplay} AWAY FROM <span style="font-family: med20;">FREE STANDARD SHIPPING</span>`;
        break;

      case leftForStandard <= 0 && leftForOvernight <= 0:
        // highest threshold reached
        $(".standardship")
          .addClass("active")
          .html('<i style="font-size:9px" class="fa fa-check"></i>');

        $(".promoship")
          .addClass("active")
          .html('<i style="font-size:9px" class="fa fa-check"></i>');

        html =
          "YOU'VE EARNED <span style='font-family: med20;'>FREE OVERNIGHT SHIPPING</span> ";

        break;

      case leftForStandard <= 0 && leftForOvernight > 0:
        // mid threshold hit
        $(".standardship")
          .addClass("active")
          .html('<i style="font-size:9px" class="fa fa-check"></i>');

        html += `YOU'RE ${leftForOvernightDisplay} AWAY FROM <span style='font-family: med20;'>FREE OVERNIGHT SHIPPING</span>`;
        break;

      case leftForStandard > 0 && leftForOvernight > 0 && response.item_count > 0:
        // no threshold hit but cart is not empty
        html += standardThreholdEqualsOvernight
          ? `YOU'RE ${leftForStandardDisplay} AWAY FROM <span style="font-family: med20;">FREE STANDARD SHIPPING</span> OR <span style='font-family: med20;'>$5 OVERNIGHT</span>`
          : `YOU'RE ${leftForStandardDisplay} AWAY FROM <span style="font-family: med20;">FREE STANDARD SHIPPING</span>`;
        break;

      case response.item_count <= 0:
        // cart is empty
        html += standardThreholdEqualsOvernight
          ? `ADD ${leftForStandardDisplay} FOR <span style="font-family: med20;">FREE STANDARD SHIPPING</span>`
          : `ADD ${leftForStandardDisplay} FOR <span style="font-family: med20;">FREE STANDARD SHIPPING</span>`;

        break;

      default:
        break;
    }

    $("#to-free-ship").html(html);
  }

  $(".toggle-quantity").click(function () {
    var currentqty = parseInt($(this).attr("data-current-qty"));
    var currentKey = $(this).attr("data-cart-key");
    var type = $(this).attr("data-type");

    var parentID = Number($(this).attr("data-parent-id"));

    var newqty = false;
    if (type == "add") newqty = currentqty + 1;
    if (type == "remove") newqty = currentqty - 1;
    if (type == "force-remove") newqty = 0;

    const { restricted_products } = JSON.parse(
      document.querySelector("[data-restricted-products]").textContent
    );

    if (newqty > 10) {
      //alert('You reached maximum allowance!');
      var parent = $(this).parent();
      parent.prepend(
        '<div class="sorry-message">Sorry, you can not add more than 10 of this product</div>'
      );
      window.setTimeout(function () {
        $(".sorry-message").remove();
      }, 2500);
    } else if (restricted_products.includes(parentID)) {
      let variantInCartCount = 0;

      for (const item of response.items) {
        if (item.product_id === parentID) {
          variantInCartCount += item.quantity;
        }
      }

      if (variantInCartCount >= 3) {
        var parent = $(this).parent();
        parent.prepend(`<div class="sorry-message" style="border: 1px solid black;font-size: 10px;padding: 1rem;margin-bottom: 1rem; font-family: 'light20'">${restrictedProductMessage}</div>`);
        window.setTimeout(function () {
          $(".sorry-message").remove();
        }, 5000);
      } else {
        DK.updateCartByLineKey(currentKey, newqty);
      }
    } else {
      DK.updateCartByLineKey(currentKey, newqty);
    }
  });

  $("#checkout").click(function () {
    var envelope = {};
    envelope.type = "event";
    envelope.name = "initiateCheckout";
    envelope.is_mobile = DK.isMobile;
    envelope.uid = DK.whoami();
    envelope.payload = {};
    DK.api("POST", "/track", envelope, function (response) {});
    return true;
  });

  /* for cart recommendations */
  if (DK.getQuery("pwt")) {
    DK.set("pwt", 1);
  }
  var pwt = DK.get("pwt");
  pwt = false;
  if (pwt) {
    var xo = [];
    for (var item in response.items) {
      if (typeof response.items[item].properties.pid == "number") {
        xo.push(response.items[item].properties.pid);
      }
    }

    for (var item in response.items) {
      if (item == 0) {
        GetPID = response.items[item].properties.pid;
        var uri = "/recommendations/rel_" + GetPID + "?";
        uri +=
          "xo=" +
          xo.join(",") +
          "&c=" +
          Shopify.currency.active +
          "|" +
          Shopify.currency.rate +
          "|" +
          Shopify.country;
        DK.api("GET", uri, function (response) {
          if (response.data2.length > 0) {
            DK.writeMatrix(response.data2);
            DK.renderCarousel($("#complete-look-items"), "cart", response.data2, function () {
              $("#complete-look").show();
            });
          }
        });
      }
    }
  }

  const cartDrawer = document.querySelector(".drawer1");
  cartDrawer.dataset.testId = "cart--drawer";

  const closer = cartDrawer.querySelector(".closer");
  closer.dataset.testId = "cart--close";
};



DK.updateCartByLineKey = function (key, quantity) {
  var readdGWP = true;
  $.ajax({
    type: 'POST',
    url: window.Shopify.routes.root + 'cart/update.js',
    data: 'updates[' + key + ']=' + parseInt(quantity),
    success: function (response) {

      loyaltySetup(response).then((x) => {
        DK.renderCart();
        x.item_count = 0;
        for (var rr in x.items) {
          if (x.items[rr].product_type != "Insurance") {
            x.item_count += x.items[rr].quantity;
          }
        }
        if (x.item_count > 0) {
          $('#bag-count').html(x.item_count).closest('[data-mini-cart-btn]').addClass('not-empty');

          var emilygwp = DK.get('freegwp0205');
          emilygwp = false;
          if (emilygwp) {
            /* start part 2 gwp*/
            var GWPfound = false;
            var GWPVendorFound = false;
            if (x.item_count >= 1) {
              $.each(x.items, function () {
                if (this.id == 43692465488129) {
                  GWPfound = true;
                }
                if (this.vendor == 'Dolls Kill x Emily The Strange' && this.id != 43692465488129) {
                  GWPVendorFound = true;
                }
              });
            }
            if (GWPfound && x.item_count == 1) {
              DK.updateCart(43692465488129, 0);
              DK.renderCart();
            }
            if (GWPfound && x.original_total_price < 1) {
              DK.updateCart(43692465488129, 0);
              DK.renderCart();
            }
            if (!GWPfound && x.original_total_price >= 1) {
              if (readdGWP) {
                DK.addToCart(43692465488129, function () {
                  DK.renderCart();
                });
              }
            }
          }

          /* end part 2 gwp */
        } else {
          $('#bag-count').html(x.item_count).closest('[data-mini-cart-btn]').removeClass('not-empty');
        }
      });
    },
    dataType: 'json',
  });
};

DK.updateCart = function (vid, quantity) {  
  var readdGWP = true;
  if (vid == 43692465488129) {
    if (quantity > 1) quantity = 1;
    if (quantity == 0) readdGWP = false;
  }

  $.ajax({
    type: 'POST',
    url: window.Shopify.routes.root + 'cart/update.js',
    data: 'updates[' + vid + ']=' + parseInt(quantity),
    success: function (response) {
      loyaltySetup(response).then((x) => {
        DK.renderCart();
        x.item_count = 0;
        for (var rr in x.items) {
          if (x.items[rr].product_type != "Insurance") {
            x.item_count += x.items[rr].quantity;
          }
        }
        if (x.item_count > 0) {
          $('#bag-count').html(x.item_count).closest('[data-mini-cart-btn]').addClass('not-empty');

          var emilygwp = DK.get('freegwp0205');
          emilygwp = false;
          if (emilygwp) {
            /* start part 2 gwp*/
            var GWPfound = false;
            var GWPVendorFound = false;
            if (x.item_count >= 1) {
              $.each(x.items, function () {
                if (this.id == 43692465488129) {
                  GWPfound = true;
                }
                if (this.vendor == 'Dolls Kill x Emily The Strange' && this.id != 43692465488129) {
                  GWPVendorFound = true;
                }
              });
            }
            if (GWPfound && x.item_count == 1) {
              DK.get(43692465488129, 0);
              DK.renderCart();
            }
            /*
          if(GWPfound && !GWPVendorFound){
            DK.updateCart(43692465488129,0);
            DK.renderCart();
            
          }
          if(!GWPfound && GWPVendorFound){
            if(readdGWP){
              DK.addToCart(43692465488129,function(){
                  DK.renderCart();
              }); 
            }
          }
          */

            if (GWPfound && x.original_total_price < 1) {
              DK.updateCart(43692465488129, 0);
              DK.renderCart();
            }

            if (!GWPfound && x.original_total_price >= 1) {
              if (readdGWP) {
                DK.addToCart(43692465488129, function () {
                  DK.renderCart();
                });
              }
            }
          }

          /* end part 2 gwp */
        } else {
          $('#bag-count').html(x.item_count).closest('[data-mini-cart-btn]').removeClass('not-empty');
        }
      })
    },
    dataType: 'json',
  });
};

DK.signupForVariant = function (vid, sku, pid) {
  if (DK.get('oosSignup')) {
    const liquidElement = document.querySelector(
      '.shopify-section #product-out-of-stock-signup-content'
    );
 
    const liquidElementCopy = liquidElement.cloneNode(true);
    liquidElementCopy.classList.remove('tw-hidden');

    $('.drawer1').html(liquidElementCopy);

    var urlRec = '/stockrecommendations/' + vid + '?';
    urlRec += 'c=' + Shopify.currency.active;

    DK.api('GET', urlRec, function (response) {
      if (response.data.length >= 4) {
        var tplA =
          '{{#data}}<div data-id="{{id}}" class="product-card" data-compare-at-price="{{compare_at_price}}" data-price="{{price}}"{{#meta}} data-preorder="{{meta.products.preorder}}" data-category="{{meta.products.category}}" data-subcategory="{{meta.products.subcategory}}" data-microcategory="{{meta.products.microcategory}}" data-mpid="{{meta.products.parentId}}"{{/meta}}> <div class="card-actions"><div class="card-actions-sale" data-pid="{{id}}"></div></div><div data-test-id="cart--upsells__card" class="card-content"> <div class="image-holder"> <a href="/products/{{handle}}"> <img data-id="{{id}}" data-card-status=0 src="{{image}}" alt="" class="grid-image"> </a> </div><div class="content-holder"><div style="display:flex;justify-content:space-between;"><p class="product-title"><a href="/products/{{handle}}">{{title}}</a></p><p class="product-price desktop">{{#strike}}<span class="strike">{{compare_at_price_text}}</span>&nbsp; {{/strike}}{{price_text}}</p></div><p class="vendor-name">{{#vendor}}{{vendor}}{{/vendor}}</p><p class="product-price mobile">{{#strike}}<span class="strike">{{compare_at_price_text}}</span>&nbsp; {{/strike}}{{price_text}}</p></div></div></div>{{/data}}';
        var H1 = Hogan.compile(tplA);
        var htmlA = H1.render(response);
      } else {
        var htmlA = '';
      }

      /* var htmlA = "";
    for (var k in response.data) {
      htmlA += '<div class="product-holder"><div class="card-content"><a href="/products/'+response.data[k].handle+'"> <img src="'+response.data[k].image+'" alt="" class="grid-image"></a><div class="content-holder"><p class="product-title"><a href="/products/'+response.data[k].handle+'">'+response.data[k].title+'</a></p><p class="product-price">'+response.data[k].currency+response.data[k].price+'</p></div></div></div>';
    } */

      htmlB =
        '<style>.pdpOUTOFSTOCK_rec {max-width: 470px; width: 100%;} .pdpOUTOFSTOCK_rec .product-holder {width: 46%; float: left; margin: 2%;} .pdpOUTOFSTOCK_rec .product-holder img {width: 100%} .pdpOUTOFSTOCK_rec .content-holder a {color: black;}</style><div class="pdpOUTOFSTOCK_rec">' +
        htmlA +
        '</div>';

      $('#stock-recommendation').html(htmlB);
      $('.grid-image', $('.drawer1')).show();
      DK.sliceCollection($('.pdpOUTOFSTOCK_rec'), 2);
    });
  } else {
    const liquidElement = document.querySelector(
      '.shopify-section #product-out-of-stock-signup-content'
    );
      
    const liquidElementCopy = liquidElement.cloneNode(true);
    liquidElementCopy.classList.remove('tw-hidden');

    $('.drawer1').html(liquidElementCopy);

    const submitButton = liquidElementCopy.querySelector('#oos-signup');
    submitButton.setAttribute('data-vid', vid);
    submitButton.setAttribute('data-sku', sku);
    submitButton.setAttribute('data-pid', pid);
  }
  if (DK.email) {
    $('#oos-email').val(DK.email);
  }
  filterDrawer(true);
  var osb = $('#oos-signup');
  var orig = osb.html();
  osb.click(function () {
    $('#oos-error').html('');
    osb.html('Working...');
    var checked = $('#opt').prop('checked');
    if (checked) {
      DK.emailCapture($('#oos-email').val(), 'stock_notification');
    }

    (async () => {
      try {
        const klaviyoOptions = {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            revision: "2024-02-15",
          },
          body: JSON.stringify({
            data: {
              type: "back-in-stock-subscription",
              attributes: {
                channels: ["EMAIL"],
                profile: {
                  data: {
                    type: "profile",
                    attributes: {
                      email: $("#oos-email").val(),
                    },
                  },
                },
              },
              relationships: {
                variant: {
                  data: {
                    type: "catalog-variant",
                    id: `$shopify:::$default:::${$(this).attr("data-vid")}`,
                  },
                },
              },
            },
          }),
        };

        const klaviyoResponse = await fetch(
          "https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=bFUiBy",
          klaviyoOptions
        );

        if (klaviyoResponse.status !== 202) {
          const errorData = await klaviyoResponse.json();
          console.error("Klaviyo Out Of Stock Error: ", errorData);

          $("#oos-error").html(
            errorData?.errors[0]?.detail || "Something went wrong. Contact customer service."
          );
          osb.html(orig);
          return;
        }

        DK.api(
          "POST",
          "/stocknotifications",
          {
            email: $("#oos-email").val(),
            vid: $(this).attr("data-vid"),
            sku: $(this).attr("data-sku"),
            opt: checked,
            product_id: pid,
          },
          function (response) {
            if (!DK.email) {
              DK.set("em", $("#oos-email").val().toLowerCase());
            }

            if (response.status == 0) {
              $("#oos-error").html(response.error_message);
              osb.html(orig);
            } else {
              $("#oos-content").html(
                '<i class="fa fa-check"></i> You are in! We will let you know as soon as this product becomes available again'
              );
            }
          }
        );
      } catch (error) {
        console.error(error);

        $("#oos-error").html("Something went wrong. Contact customer service.");
        osb.html(orig);
        return;
      }
    })();
  });

  const oosDrawer = document.querySelector(".drawer1");
  oosDrawer.dataset.testId = "notify-drawer";

  const closer = oosDrawer.querySelector(".closer");
  closer.dataset.testId = "notify-drawer--close";
};

DK.renderQATC = function (element, mode) {
  $('.qatc-options', element).each(function () {
    var pid = $(this).attr('data-id');
    var newMatrix = false;
    if (typeof mode == 'string' && mode == 'mslice') {
      newMatrix = true;
    }
    if (typeof DK.newProdPage == 'number' || newMatrix) {
      cMatrix[pid] = wMatrix['w' + pid];
    }

    var tpl =
      '{{#sizes}}<div data-policy="{{policy}}" class="qatc-variant{{^quantity}} variant-disabled oos{{/quantity}}" data-pid="{{pid}}" data-vid="{{id}}" data-variant-title="{{title}}" data-sku="{{sku}}">{{#shorthand}}{{shorthand}}{{/shorthand}}{{^shorthand}}OS{{/shorthand}}{{^quantity}}<i class="fa fa-bell"></i>{{/quantity}}</div>{{/sizes}}';
    var H = Hogan.compile(tpl);
    var sizes = resortSizing(cMatrix[pid]);

    for (var i in sizes) {
      if (sizes[i].quantity < 0) sizes[i].quantity = 0;
      if (typeof sizes[i].policy == 'string' && sizes[i].policy == 'continue') {
        sizes[i].quantity = 1;
      }
    }
    var html = H.render({ sizes: sizes, pid: pid });
    $(this).html(html);
  });

  $('.qatc-variant').click(function (e) {
    e.stopImmediatePropagation();
    e.stopPropagation();
    e.preventDefault();
    $('.qatc-variant').removeClass('qatc-variant-selected');
    if (!$(this).hasClass('variant-disabled')) {
      $(this).addClass('qatc-variant-selected');
      vid = $(this).attr('data-vid');
      xpid = $(this).attr('data-pid');
      policy = $(this).attr('data-policy');

      var eobj = $('.product-card[data-id=' + xpid + ']');
      cp = eobj.attr('data-compare-at-price');
      pp = eobj.attr('data-price');

      xmultiplier = parseFloat(cp) / parseFloat(pp);
      DK.qcontext = {};
      DK.qcontext.multiplier = xmultiplier;
      DK.qcontext.op = pp;

      dc = eobj.attr('data-category');
      dsc = eobj.attr('data-subcategory');
      dmc = eobj.attr('data-microcategory');
      dpid = eobj.attr('data-mpid');
      dpre = eobj.attr('data-preorder');
      dclear = eobj.attr('data-clearance');
      if (dc) DK.qcontext.category = dc;
      if (dsc) DK.qcontext.subcategory = dsc;
      if (dmc) DK.qcontext.microcategory = dmc;
      if (dpid) DK.qcontext.pid = dpid;
      if (policy && policy == 'continue' && dpre) {
        DK.qcontext.preorder = dpre;
      }
      if (dclear) DK.qcontext.clearance = dclear;

      DK.addToCart(vid, function (response) {
        DK.renderCart(response);
      });
    }
    if ($(this).hasClass('oos')) {
      DK.signupForVariant(
        $(this).attr('data-vid'),
        $(this).attr('data-sku'),
        $(this).attr('data-pid')
      );
    }
  });

  $('.qatc').mouseover(function (e) {
    e.stopImmediatePropagation();
    e.stopPropagation();
    e.preventDefault();

    var pid = $(this).attr('data-id');
    $('.qatc-options[data-id="' + pid + '"]').slideDown();
  });

  $('.qatc').click(function (e) {
    e.stopImmediatePropagation();
    e.stopPropagation();
    e.preventDefault();
    if (!DK.isMobile) return;

    var pid = $(this).attr('data-id');
    $(this).addClass('opened');
    $('.qatc-options[data-id="' + pid + '"]').slideDown();

    $('.qatc-options[data-id!="' + pid + '"]').hide();
  });

  $('.qatc').mouseleave(function () {
    if (DK.isMobile) return;
    var pid = $(this).attr('data-id');
    $('.qatc-options[data-id="' + pid + '"]').slideUp();
    window.setTimeout(function () {
      $(element).removeClass('opened-qatc');
      $('.qatc-options[data-id="' + pid + '"]').removeClass('opened-qatc');
      $('.confirm-add-to-cart').fadeOut();
    }, 150);
  });
};

DK.quickAddToBag = function (id) {
  var tpl =
    '<div class="centered mb30"><p class="drawer1-header">Quick Add To Bag</p></div><div id="qatc-content"><p>Choose your size to add this item to your bag</p><div id="cart-options">{{#sizes}}<div class="size-box{{^quantity}} oos{{/quantity}}" data-vid="{{id}}" data-sku="{{sku}}">{{title}}{{^quantity}} <i class="fa fa-bell"></i>{{/quantity}}</div>{{/sizes}}</div></div>';

  var H = Hogan.compile(tpl);
  var sizes = resortSizing(cMatrix[id]);
  var html = H.render({ sizes: sizes });
  $('.drawer1').html(html);
  filterDrawer(true);

  $('.size-box', $('#qatc-content')).click(function () {
    if ($(this).hasClass('oos')) {
      DK.signupForVariant(
        $(this).attr('data-vid'),
        $(this).attr('data-sku'),
        $(this).attr('data-pid')
      );
    } else {
      $('.size-box.selected').removeClass('selected');
      $(this).addClass('selected');
      var vid = $(this).attr('data-vid');
      DK.addToCart(vid, function (response) {
        filterDrawer(false);
      });
    }
  });
};

DK.setTimer = function (target) {
  var t = Math.round(Date.now() / 1000);
  var until = target - t;
  if (until < 0) {
    $('#skinny-timer').remove();
  }
  var hrs = Math.floor(until / (60 * 60));
  var rem = until - hrs * 60 * 60;

  var mins = Math.floor(rem / 60);
  var rem = rem - mins * 60;

  hrs = hrs.toString();
  mins = mins.toString();
  rem = rem.toString();

  if (hrs.length == 1) hrs = '0' + hrs;
  if (mins.length == 1) mins = '0' + mins;
  if (rem.length == 1) rem = '0' + rem;

  $('#sk-hr').html(hrs);
  $('#sk-min').html(mins);
  $('#sk-sec').html(rem);
  window.setTimeout(function () {
    DK.setTimer(target);
  }, 1000);
};

DK.rotating = false;
DK.pt = 5000;
DK.promoRotate = function (isAdvance, timeout) {
  DK.rotating = true;
  nth = DK.currentPromo;
  barObj = $('#airplane span').eq(nth);
  where = isAdvance ? { left: -750 } : { right: -750 };
  if ($('#airplane span').length > 1) {
    barObj.animate(where, 3400, function () {
      DK.displayPromo(isAdvance, timeout);
      barObj.addClass('inactive');
      barObj.css('left', 'auto');
      barObj.css('right', 'auto');
    });
  }
  if (typeof DK.promoTime != 'number') {
    DK.promoTime = window.setTimeout(function () {
      DK.promoRotate(true);
    }, DK.pt);
  }
};

DK.displayPromo = function (isAdvance, timeout) {
  if (isAdvance) nextObj = DK.currentPromo + 1;
  else nextObj = DK.currentPromo - 1;

  totalCount = $('#airplane span').length - 1;

  if (nextObj > totalCount) nextObj = 0;
  if (nextObj < 0) nextObj = totalCount;
  DK.currentPromo = nextObj;
  psObj = $('#airplane span').eq(nextObj);

  //psObj.removeClass('inactive');
  psObj.css('right', '-750px');
  psObj.animate({ left: 0 }, 3400, function () {
    DK.displayPromo(isAdvance, timeout);
  });
  DK.rotating = false;
  if (typeof timeout != 'boolean') {
    DK.promoTime = window.setTimeout(function () {
      DK.promoRotate(true);
    }, DK.pt);
  }
};

DK.currentPromo = -1;

DK.airplanes = function (market) {
  $('#airplane-wrapper')?.slick && $('#airplane-wrapper').slick({
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    vertical: true,
    arrows: false,
    pauseOnHover: true,
    pauseOnFocus: true,
    autoplaySpeed: 5000,
  });

  window.setTimeout(function () {
    $('#airplane span').fadeIn();
  }, 100);

  return;
  nextObj = DK.currentPromo + 1;
  psObj = $('#airplane span').eq(nextObj);
  psObj.css('left', '0px');
  return;
  DK.promoTime = window.setTimeout(function () {
    DK.promoRotate(true);
  }, DK.pt);

  if ($('#airplane-wrapper span').length > 1) {
    /* $('.airplanecontrols').show();
          $('i.prh').click(function() {
          if (DK.rotating) return;
          window.clearTimeout(DK.promoTime);
            DK.promoTime = false;
          DK.promoRotate(true, false);
          });
          $('i.ple').click(function() {
          if (DK.rotating) return;
          window.clearTimeout(DK.promoTime);
            DK.promoTime = false;
          DK.promoRotate(false, false);
          }); */
  }
};

DK.couponOverride = true;
DK.getBOGOCoupon = function () {
  var check = DK.getQuery('bogo50coupon');
  if (check == 1) DK.set('bogo_coupon', 1);
  var r = DK.get('bogo_coupon');
  if (r && DK.couponOverride) {
    if (DK.isMobile) {
      var msg = 'BOGO 50% OFF W/ CODE: BOGO50';
    } else {
      var msg = 'BOGO 50% OFF W/ CODE: BOGO50';
    }

    var time = new Date().getTime();
    time = time / 1000;

    var endTimer = 1662015600;

    if (time < endTimer) {
      $('#skinny').remove();
      $('#header-expanded').after(
        '<style>#skinny-coupon:hover{cursor:pointer}</style><div id="skinny-coupon" data-market="US" style="background-color: red; color: white; width: 100%; display: block;"><strong>' +
          msg +
          ' </strong><p id="skinny-coupon-timer" data-countdown="' +
          endTimer +
          '">Ends In <span id="sk-hr"></span> : <span id="sk-min"></span> : <span id="sk-sec"></span></p></div>'
      );
      $('#skinny-coupon').click(function () {
        window.location = 'https://www.dollskill.com/collections/our-favs-2?sale=1';
      });
      DK.setTimer(endTimer);
    }
  }
};
DK.getSelfCoupon = function () {
  return;
  var r = DK.get('coupon_pop');
  if (r && DK.couponOverride) {
    if (typeof r.data.discount_amount != 'string') {
      r.data.discount_amount = '20';
      r.data.discount_type = 'percent';
    }

    if (typeof r.data.discount_amount == 'string') {
      var type = '';
      if (r.data.discount_type == 'percent') type = '%';

      if (
        r.data.coupon_code == 'XXTRA10' ||
        r.data.coupon_code == 'XXTRA20' ||
        r.data.coupon_code == 'XXTRA25'
      ) {
        if (DK.isMobile) {
          var msg =
            r.data.discount_amount + '' + type + ' OFF CLEARANCE - USE CODE: ' + r.data.coupon_code;
        } else {
          var msg =
            r.data.discount_amount + '' + type + ' OFF CLEARANCE - USE CODE: ' + r.data.coupon_code;
        }
      } else {
        if (DK.isMobile) {
          var msg = r.data.discount_amount + '' + type + ' OFF USE CODE: ' + r.data.coupon_code;
        } else {
          var msg =
            r.data.discount_amount + '' + type + ' OFF UR ORDER USE CODE: ' + r.data.coupon_code;
        }
      }
      $('#pre-nav-skinny').html(msg);
      $('#pre-nav-skinny').show();
    } else {
      DK.api('GET', '/coupons?coupon_code=' + r.data.coupon_code, function (r) {
        if (r.status == 1) {
          DK.set('coupon_pop', r);
          DK.getSelfCoupon();
        }
      });
    }

    return;
    var time = new Date().getTime();
    time = Math.floor(time / 1000);
    if (r.data.end >= time) {
      $('#skinny').remove();
      $('#header-expanded').after(
        '<div id="skinny-coupon" data-market="US" style="background-color: #83f7f3; color: black; width: 100%; display: block;"><strong>20% OFF COUPON: ' +
          r.data.coupon_code +
          ' </strong><p id="skinny-coupon-timer" data-countdown="' +
          r.data.end +
          '">Ends In <span id="sk-hr"></span> : <span id="sk-min"></span> : <span id="sk-sec"></span></p></div>'
      );
      DK.setTimer(r.data.end);
    }
  }
};

DK.globalBanners = function (market) {
  var preview = DK.getQuery('_spreview');
  var fetch;
  var mta = window.location.pathname;

  if (preview) {
    fetch = '/banners/' + preview;
    $('body').append(
      '<div style="z-index: 99999; position: fixed; top: 20px; right: 20px; display: inline; width: 400px; text-align: center; background-color: yellow; padding: 10px;">In-Preview Mode</div>'
    );
    if ($('#skinny').length == 0) {
      $("<div id='skinny'></div>").insertAfter('#header-expanded');
    }

    $('#skinny').html('');
    DK.api('GET', fetch, function (response) {
      var timer = false;
      if (response.status == 1) {
        if (typeof response.data == 'object') {
          $('#skinny').css('background-color', response.data.bgcolor);
          $('#skinny').css('color', response.data.tcolor);
          var html = '<strong>' + response.data.content + '</strong>';
          if (response.data.countdown_to > 0) {
            var t = Math.round(Date.now() / 1000);
            if (t < response.data.countdown_to) {
              html +=
                '<p id="skinny-timer">Ends In <span id="sk-hr"></span> : <span id="sk-min"></span> : <span id="sk-sec"></span></p>';
              timer = true;
            }
          }
          $('#skinny').html(html);

          $('#skinny').show();
          if (timer) {
            DK.setTimer(response.data.countdown_to);
          }
        }
      }
    });
  } else {
    var st = $('#skinny-timer');
    if (st.length == 1) {
      var cd = parseInt(st.attr('data-countdown'));
      if (cd > 0) {
        DK.setTimer(cd);
      }
    }
  }
};
DK.touchpoint = function () {
  var utest = DK.getQuery('utm_source');
  touch = false;
  if (utest) {
    touch = {};
    touch.source = utest;
    touch.medium = DK.getQuery('utm_medium');
    touch.campaign = DK.getQuery('utm_campaign');
  } else {
    var utest = document.referrer;
    if (utest) {
      utest = utest.split('/');
      if (typeof utest[2] == 'string') {
        var refdomain = utest[2];
        if (refdomain.indexOf('dollskill.com') == -1) {
          touch = {};
          touch.source = 'referrer';
          touch.medium = refdomain;
        }
      }
    }
  }
  if (typeof touch == 'object') {
    touch.timestamp = Math.round(Date.now() / 1000);
    var utms = DK.get('slist');
    if (!utms) utms = [];
    utms.push(touch);
    DK.set('slist', utms);
  }
};
DK.emailCapture = function (email, location, callback) {
  var packet = {};
  packet.email = email.toLowerCase();
  packet.location = location;
  packet.uid = DK.whoami();
  packet.marketing_source = {};
  var mtest = DK.get('slist');
  if (typeof mtest == 'object') {
    packet.marketing_source = mtest[0];
  }

  DK.api('POST', '/ecapture', packet, function (r) {
    if (r.status == 1) {
      DK.set('em', packet.email);
      DK.email = packet.email;
    }
    if (typeof callback == 'function') {
      callback(r);
    }
  });
};
DK.subscribe('domLoaded', function () {
  DK.init();

  DK.getCart(function (cart) {});
  $('#rendercart').click(function (e) {
    if (location.href.indexOf('cart?fallback=true') >= 0) {
      location.reload();
    } else {
      e.preventDefault();
      DK.renderCart();
    }
  });

  DK.getSelfCoupon();
  DK.getBOGOCoupon();
});

if (DK.getQuery('cart')) {
  document.addEventListener("DOMContentLoaded", () => {
    DK.renderCart();
  })
}

var coupTest = DK.getQuery('ecoupon');
if (coupTest) {
  var r = {};
  r.data = {};
  r.data.coupon_code = coupTest;

  da = DK.getQuery('eda');
  if (da) {
    r.data.discount_amount = da;
    r.data.discount_amount = da;
    r.data.discount_type = 'percent';
  }
  DK.set('coupon_pop', r);
  DK.getSelfCoupon();
}

DK.touchpoint();
